import React, { useRef, useState } from 'react';
import authService from '../../services/authService';
import CreditPurchaseModal from './CreditPurchaseModal';
import CreditAllocationModal from './CreditAllocationModal';
import CreditHistory from './CreditHistory';
import CreditExpiration from './CreditExpiration';
import AutoRechargeRules from './AutoRechargeRules';
import RolesPermissionsTab from './RolesPermissionsTab';
import AuthenticationTab from './AuthenticationTab';
import AccessLogs from './AccessLogs';
import ScriptGovernanceTab from './ScriptGovernanceTab';
import ScriptPermissionsTab from './ScriptPermissionsTab';
import SubscriptionManagement from './SubscriptionManagement';
import RealTimeDashboard from './RealTimeDashboard';
import AlertingSystem from './AlertingSystem';
import { getTabStyles, getTabHoverStyles, restoreTabStyles } from '../../utils/tabColors';

// Chart components
function MiniLineChart({ points, height = 120, color = '#4c51bf', fill = 'rgba(76, 81, 191, 0.12)' }) {
  const containerRef = useRef(null);
  const [tip, setTip] = useState(null);
  if (!points || points.length === 0) {
    return (
      <div style={{ height, display:'flex', alignItems:'center', justifyContent:'center', color:'#6b7280', fontSize:12 }}>No data</div>
    );
  }
  const width = Math.max(200, points.length * 36);
  const maxVal = Math.max(1, ...points.map(p => p.value));
  const minVal = 0;
  const xStep = width / Math.max(1, points.length - 1);
  const yScale = (v) => height - (v - minVal) / (maxVal - minVal || 1) * (height - 20) - 10;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * xStep},${yScale(p.value)}`).join(' ');
  const area = `M 0,${height} ${path} L ${width},${height} Z`;
  const onMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xPx = e.clientX - rect.left;
    const xView = (xPx / rect.width) * width;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round(xView / (xStep || 1))));
    const px = idx * xStep;
    const py = yScale(points[idx].value);
    setTip({ x: px, y: py, label: points[idx].label, value: points[idx].value, idx });
  };
  const onLeave = () => setTip(null);
  const cw = containerRef.current?.clientWidth || 0;
  const tipLeft = tip ? Math.min(Math.max(8, (tip.x / width) * (cw || 0) + 10), (cw || 0) - 160) : 0;
  const tipTop = tip ? Math.max(8, (tip.y / height) * height - 34) : 0;
  const xTickIdx = [0, Math.round((points.length - 1) / 2), points.length - 1].filter((v, i, arr) => v >= 0 && v < points.length && arr.indexOf(v) === i);
  const yTickVals = [0, Math.round(maxVal * 0.5), maxVal];
  return (
    <div ref={containerRef} style={{ position: 'relative', border:'1px solid #bfdbfe', borderRadius:8, background:'#eff6ff' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }} onMouseMove={onMove} onMouseLeave={onLeave}>
        {yTickVals.map((v, idx) => (
          <g key={`y-${idx}`}>
            <line x1={0} x2={width} y1={yScale(v)} y2={yScale(v)} stroke="#e0efff" />
            <text x={4} y={yScale(v) - 4} fontSize="10" fill="#64748b">{v}</text>
          </g>
        ))}
        <path d={area} fill={fill} stroke="none" />
        <path d={path} fill="none" stroke={color} strokeWidth="2" />
        {xTickIdx.map((i) => (
          <text key={`x-${i}`} x={i * xStep} y={height - 2} fontSize="10" textAnchor="middle" fill="#64748b">{points[i].label}</text>
        ))}
        {tip && (
          <g>
            <line x1={tip.x} x2={tip.x} y1={0} y2={height} stroke="#9ca3af" strokeDasharray="4 4" />
            <circle cx={tip.x} cy={tip.y} r={3.5} fill={color} />
          </g>
        )}
      </svg>
      {tip && (
        <div style={{ position:'absolute', left: tipLeft, top: tipTop, background:'#111827', color:'#fff', fontSize:12, padding:'6px 8px', borderRadius:6, boxShadow:'0 4px 12px rgba(0,0,0,0.2)', pointerEvents:'none' }}>
          <div style={{ fontWeight:600 }}>{tip.label}</div>
          <div>Value: {tip.value.toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}

function HorizontalBarChart({ items, height = 200, color = '#059669' }) {
  const containerRef = useRef(null);
  const [tip, setTip] = useState(null);
  if (!items || items.length === 0) {
    return (
      <div style={{ height, display:'flex', alignItems:'center', justifyContent:'center', color:'#6b7280', fontSize:12 }}>No data</div>
    );
  }
  const maxVal = Math.max(1, ...items.map(i => i.value));
  const barHeight = Math.max(20, (height - (items.length - 1) * 8) / items.length);
  const onMoveBar = (e, label, value) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xPx = e.clientX - rect.left;
    const yPx = e.clientY - rect.top;
    setTip({ x: xPx, y: yPx, label, value });
  };
  const onLeave = () => setTip(null);
  const cw = containerRef.current?.clientWidth || 0;
  const tipLeft = tip ? Math.min(Math.max(8, tip.x + 10), (cw || 0) - 180) : 0;
  const tipTop = tip ? Math.max(8, tip.y - 34) : 0;
  return (
    <div ref={containerRef} style={{ position:'relative', border:'1px solid #bfdbfe', borderRadius:8, background:'#eff6ff', padding:'12px' }}>
      <svg viewBox={`0 0 600 ${height}`} style={{ width: '100%', height }} onMouseLeave={onLeave}>
        {items.map((i, idx) => {
          const barWidth = Math.round((i.value / maxVal) * 550);
          const y = idx * (barHeight + 8);
          return (
            <g key={i.label || idx}>
              <rect x={0} y={y} width={barWidth} height={barHeight} fill={color} rx="4" onMouseMove={(e)=>onMoveBar(e, i.label || 'Item', i.value)} />
              <text x={barWidth + 8} y={y + barHeight / 2 + 4} fontSize="11" fill="#111827" dominantBaseline="middle">{i.label || 'Item'}</text>
            </g>
          );
        })}
      </svg>
      {tip && (
        <div style={{ position:'absolute', left: tipLeft, top: tipTop, background:'#111827', color:'#fff', fontSize:12, padding:'6px 8px', borderRadius:6, boxShadow:'0 4px 12px rgba(0,0,0,0.2)', pointerEvents:'none', maxWidth: 180 }}>
          <div style={{ fontWeight:600, marginBottom:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{tip.label}</div>
          <div>Value: {tip.value.toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}


// Cost Analytics Component
function CostAnalytics({ orgId }) {
  const [loading, setLoading] = React.useState(false);
  const [analyticsData, setAnalyticsData] = React.useState(null);
  const [period, setPeriod] = React.useState('30d'); // 7d, 30d, 90d, mtd

  React.useEffect(() => {
    if (orgId) {
      loadAnalyticsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, period]);

  const loadAnalyticsData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const t = authService.getToken();
      // Use existing usage endpoints
      const [dashboardRes, trendsRes, topConsumersRes] = await Promise.all([
        fetch(`/api/orgs/${orgId}/usage/dashboard`, {
          headers: t ? { Authorization: `Bearer ${t}` } : {}
        }),
        fetch(`/api/orgs/${orgId}/usage/trends?period=${period}`, {
          headers: t ? { Authorization: `Bearer ${t}` } : {}
        }),
        fetch(`/api/orgs/${orgId}/usage/top-consumers?type=models&limit=10`, {
          headers: t ? { Authorization: `Bearer ${t}` } : {}
        })
      ]);

      if (dashboardRes.ok && trendsRes.ok && topConsumersRes.ok) {
        const [dashboard, trends, topConsumers] = await Promise.all([
          dashboardRes.json(),
          trendsRes.json(),
          topConsumersRes.json()
        ]);

        setAnalyticsData({
          dashboard,
          trends,
          topConsumers
        });
      }
    } catch (e) {
      console.error('Failed to load analytics data:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!orgId) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Please select an organization</div>;
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading analytics...</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 0 }}>Cost Analytics</h2>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1.5px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#fff',
            cursor: 'pointer'
          }}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="mtd">Month to date</option>
        </select>
      </div>

      {/* Cost Summary */}
      {analyticsData?.dashboard && (
        <div className="dashboard-card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Cost Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Total Cost (Current Month)</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>
                ${((analyticsData.dashboard.totalCostCents || 0) / 100).toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Total Requests</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>
                {(analyticsData.dashboard.totalRequests || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Total Tokens</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>
                {((analyticsData.dashboard.totalInputTokens || 0) + (analyticsData.dashboard.totalOutputTokens || 0)).toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Average Cost/Request</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>
                ${analyticsData.dashboard.totalRequests > 0 
                  ? ((analyticsData.dashboard.totalCostCents || 0) / analyticsData.dashboard.totalRequests / 100).toFixed(4)
                  : '0.0000'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Trends Chart */}
      {analyticsData?.trends && analyticsData.trends.length > 0 && (
        <div className="dashboard-card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Cost Trends</h3>
          <MiniLineChart
            points={analyticsData.trends.map(t => ({
              label: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              value: (t.costCents || 0) / 100
            }))}
            height={200}
            color="#667eea"
            fill="rgba(102, 126, 234, 0.12)"
          />
        </div>
      )}

      {/* Top Consumers by Model */}
      {analyticsData?.topConsumers && analyticsData.topConsumers.length > 0 && (
        <div className="dashboard-card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Top Models by Cost</h3>
          <HorizontalBarChart
            items={analyticsData.topConsumers.map(c => ({
              label: c.model || 'Unknown',
              value: (c.costCents || 0) / 100
            }))}
            height={Math.min(400, analyticsData.topConsumers.length * 40)}
            color="#059669"
          />
        </div>
      )}

      {/* Cost Breakdown by Provider */}
      {analyticsData?.dashboard && (
        <div className="dashboard-card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Cost Breakdown by Provider</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {analyticsData.dashboard.byProvider && analyticsData.dashboard.byProvider.length > 0 ? (
              analyticsData.dashboard.byProvider.map((provider, idx) => (
                <div
                  key={provider.provider || idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    gap: 12,
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{provider.provider || 'Unknown'}</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Cost</div>
                    <div style={{ fontWeight: 600 }}>${((provider.costCents || 0) / 100).toFixed(2)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Requests</div>
                    <div>{(provider.requests || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Avg/Request</div>
                    <div>${provider.requests > 0 ? ((provider.costCents || 0) / provider.requests / 100).toFixed(4) : '0.0000'}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No provider data available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Billing Settings Component
function BillingSettings({ orgId }) {
  const [loading, setLoading] = React.useState(false);
  const [settings, setSettings] = React.useState({
    billingEmail: '',
    billingAddress: '',
    taxId: '',
    currency: 'USD',
    invoiceFrequency: 'monthly',
    autoPay: false
  });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (orgId) {
      loadSettings();
    }
  }, [orgId]);

  const loadSettings = async () => {
    // TODO: Create backend endpoint GET /api/orgs/{id}/billing/settings
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // TODO: Create backend endpoint PUT /api/orgs/{id}/billing/settings
      alert('Settings saved (backend endpoint needed)');
    } catch (e) {
      console.error('Failed to save settings:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 12 }}>Billing Settings</h2>
      
      <div className="dashboard-card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Billing Information</h3>
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Billing Email</label>
            <input
              type="email"
              value={settings.billingEmail}
              onChange={(e) => setSettings({ ...settings, billingEmail: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
              placeholder="billing@example.com"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Billing Address</label>
            <textarea
              value={settings.billingAddress}
              onChange={(e) => setSettings({ ...settings, billingAddress: e.target.value })}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
              placeholder="Street address, City, State, ZIP"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Tax ID</label>
              <input
                type="text"
                value={settings.taxId}
                onChange={(e) => setSettings({ ...settings, taxId: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                placeholder="EIN or Tax ID"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Currency</label>
              <select
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Invoice Frequency</label>
              <select
                value={settings.invoiceFrequency}
                onChange={(e) => setSettings({ ...settings, invoiceFrequency: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.autoPay}
                  onChange={(e) => setSettings({ ...settings, autoPay: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, fontWeight: 500 }}>Enable Auto-Pay</span>
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '10px 20px', fontSize: 14 }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Invoices & Receipts Component
function InvoicesReceipts({ orgId }) {
  const [loading, setLoading] = React.useState(false);
  const [invoices, setInvoices] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(50);
  const [total, setTotal] = React.useState(0);

  React.useEffect(() => {
    if (orgId) {
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, page, pageSize]);

  const loadInvoices = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const t = authService.getToken();
      // Use SubscriptionManager's billing history endpoint
      const res = await fetch(`/api/credits/subscription/billing-history?organizationId=${orgId}&page=${page}&pageSize=${pageSize}`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to load invoices:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (cents) => {
    return `$${((cents || 0) / 100).toFixed(2)}`;
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 12 }}>Invoices & Receipts</h2>
      
      {orgId ? (
        <>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading invoices...</div>
          ) : invoices.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No invoices found</div>
          ) : (
            <>
              <div className="table-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Paid At</th>
                      <th>Invoice ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.billingId}>
                        <td>
                          {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                        </td>
                        <td>{formatCurrency(invoice.totalCents)}</td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: 12,
                            fontWeight: 500,
                            background: invoice.status === 'paid' ? '#05966920' : '#f59e0b20',
                            color: invoice.status === 'paid' ? '#059669' : '#f59e0b'
                          }}>
                            {invoice.status?.toUpperCase()}
                          </span>
                        </td>
                        <td>{invoice.paidAt ? formatDate(invoice.paidAt) : '-'}</td>
                        <td style={{ fontSize: 12, fontFamily: 'monospace' }}>
                          {invoice.stripeInvoiceId || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="admin-pager" style={{ marginTop: 16, justifyContent: 'space-between' }}>
                <div style={{ color: '#6b7280', fontSize: 14 }}>
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} invoices
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: page === 1 ? '#f3f4f6' : '#fff',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      fontSize: 14
                    }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page * pageSize >= total}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      background: page * pageSize >= total ? '#f3f4f6' : '#fff',
                      cursor: page * pageSize >= total ? 'not-allowed' : 'pointer',
                      fontSize: 14
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          Invoice history is available for organizations with subscriptions
        </div>
      )}
    </div>
  );
}

// Budget Controls Component
function BudgetControls({ orgId }) {
  const [loading, setLoading] = React.useState(false);
  const [orgBudget, setOrgBudget] = React.useState(null);
  const [userBudgets, setUserBudgets] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedUsers, setSelectedUsers] = React.useState(new Set());
  const [editingUser, setEditingUser] = React.useState(null);
  const [showBulkModal, setShowBulkModal] = React.useState(false);
  const [bulkType, setBulkType] = React.useState(null);
  const [bulkValue, setBulkValue] = React.useState('');
  const [toast, setToast] = React.useState({ show: false, message: '', type: 'success' });

  React.useEffect(() => {
    if (orgId) {
      loadBudgets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadBudgets = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const t = authService.getToken();
      
      const orgRes = await fetch(`/api/admin/organizations/${orgId}/budget-controls/organization`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (orgRes.ok) {
        const orgData = await orgRes.json();
        setOrgBudget(orgData);
      }

      const usersRes = await fetch(`/api/admin/organizations/${orgId}/budget-controls/users`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUserBudgets(Array.isArray(usersData) ? usersData : []);
      }
    } catch (e) {
      console.error('Failed to load budgets:', e);
      setToast({ show: true, type: 'error', message: 'Failed to load budget data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrgBudget = async (type, value) => {
    if (!orgId) return;
    setSaving(true);
    try {
      const t = authService.getToken();
      const body = type === 'daily' 
        ? { dailyLimitCredits: value, monthlyLimitCredits: orgBudget?.monthlyLimitCredits, alertThresholdPercent: orgBudget?.alertThresholdPercent || 80 }
        : type === 'monthly'
        ? { dailyLimitCredits: orgBudget?.dailyLimitCredits, monthlyLimitCredits: value, alertThresholdPercent: orgBudget?.alertThresholdPercent || 80 }
        : { dailyLimitCredits: orgBudget?.dailyLimitCredits, monthlyLimitCredits: orgBudget?.monthlyLimitCredits, alertThresholdPercent: value };
      
      const res = await fetch(`/api/admin/organizations/${orgId}/budget-controls/organization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        setEditing(null);
        await loadBudgets();
        setToast({ show: true, type: 'success', message: 'Organization budget updated' });
      } else {
        const error = await res.json().catch(() => ({ message: 'Failed to save budget' }));
        throw new Error(error.message || 'Failed to save budget');
      }
    } catch (e) {
      console.error('Failed to save budget:', e);
      setToast({ show: true, type: 'error', message: e.message || 'Failed to save budget' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUserBudget = async (userId, data) => {
    if (!orgId) return;
    setSaving(true);
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/admin/organizations/${orgId}/budget-controls/users/${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        setEditingUser(null);
        await loadBudgets();
        setToast({ show: true, type: 'success', message: 'User budget updated' });
      } else {
        const error = await res.json().catch(() => ({ message: 'Failed to save user budget' }));
        throw new Error(error.message || 'Failed to save user budget');
      }
    } catch (e) {
      console.error('Failed to save user budget:', e);
      setToast({ show: true, type: 'error', message: e.message || 'Failed to save user budget' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUserBudget = async (userId) => {
    if (!orgId) return;
    if (!window.confirm('Remove budget limits for this user? They will inherit organization defaults.')) return;
    
    setSaving(true);
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/admin/organizations/${orgId}/budget-controls/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      
      if (res.ok) {
        await loadBudgets();
        setToast({ show: true, type: 'success', message: 'User budget limits removed' });
      } else {
        throw new Error('Failed to remove user budget');
      }
    } catch (e) {
      console.error('Failed to delete user budget:', e);
      setToast({ show: true, type: 'error', message: 'Failed to remove user budget' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetUserSpend = async (userId) => {
    if (!orgId) return;
    if (!window.confirm('Reset current period spend for this user? This will clear their daily and monthly spend tracking.')) return;
    
    setSaving(true);
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/admin/organizations/${orgId}/budget-controls/users/${encodeURIComponent(userId)}/reset`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      
      if (res.ok) {
        await loadBudgets();
        setToast({ show: true, type: 'success', message: 'User budget spend reset' });
      } else {
        throw new Error('Failed to reset user spend');
      }
    } catch (e) {
      console.error('Failed to reset user spend:', e);
      setToast({ show: true, type: 'error', message: 'Failed to reset user spend' });
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSet = async () => {
    if (!orgId || selectedUsers.size === 0) return;
    if (!bulkValue || parseFloat(bulkValue) <= 0) {
      setToast({ show: true, type: 'error', message: 'Please enter a valid amount' });
      return;
    }

    setSaving(true);
    try {
      const t = authService.getToken();
      const body = {
        userIds: Array.from(selectedUsers),
        [bulkType === 'daily' ? 'dailyLimitCredits' : 'monthlyLimitCredits']: parseFloat(bulkValue)
      };
      
      const res = await fetch(`/api/admin/organizations/${orgId}/budget-controls/users/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        const result = await res.json();
        setShowBulkModal(false);
        setBulkValue('');
        setSelectedUsers(new Set());
        await loadBudgets();
        setToast({ show: true, type: 'success', message: `Budget limits updated for ${result.count || selectedUsers.size} users` });
      } else {
        const error = await res.json().catch(() => ({ message: 'Failed to set bulk limits' }));
        throw new Error(error.message || 'Failed to set bulk limits');
      }
    } catch (e) {
      console.error('Failed to set bulk limits:', e);
      setToast({ show: true, type: 'error', message: e.message || 'Failed to set bulk limits' });
    } finally {
      setSaving(false);
    }
  };

  const handleBulkClear = async () => {
    if (!orgId || selectedUsers.size === 0) return;
    if (!window.confirm(`Remove budget limits for ${selectedUsers.size} selected users?`)) return;

    setSaving(true);
    try {
      const t = authService.getToken();
      let successCount = 0;
      let failCount = 0;

      for (const userId of selectedUsers) {
        try {
          const res = await fetch(`/api/admin/organizations/${orgId}/budget-controls/users/${encodeURIComponent(userId)}`, {
            method: 'DELETE',
            headers: t ? { Authorization: `Bearer ${t}` } : {}
          });
          if (res.ok) successCount++;
          else failCount++;
        } catch {
          failCount++;
        }
      }

      setSelectedUsers(new Set());
      await loadBudgets();
      setToast({ show: true, type: successCount > 0 ? 'success' : 'error', message: `Removed limits for ${successCount} users${failCount > 0 ? `, ${failCount} failed` : ''}` });
    } catch (e) {
      console.error('Failed to clear bulk limits:', e);
      setToast({ show: true, type: 'error', message: 'Failed to clear limits' });
    } finally {
      setSaving(false);
    }
  };


  const getSpendPercentage = (spend, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.min(100, (spend / limit) * 100);
  };

  const getSpendColor = (percentage) => {
    if (percentage >= 95) return '#dc2626';
    if (percentage >= 80) return '#f59e0b';
    if (percentage >= 50) return '#eab308';
    return '#059669';
  };

  const filteredUsers = userBudgets.filter(u => 
    !searchTerm || 
    (u.userName && u.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (u.userEmail && u.userEmail.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading && !orgBudget) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading budget controls...</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          padding: '12px 20px',
          background: toast.type === 'success' ? '#059669' : '#dc2626',
          color: 'white',
          borderRadius: '8px',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {toast.message}
          <button onClick={() => setToast({ ...toast, show: false })} style={{ marginLeft: 12, background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>×</button>
        </div>
      )}

      <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 12 }}>Budget Controls</h2>
      
      <div style={{ display: 'grid', gap: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Organization Budget Limits</h3>
        
        <div className="dashboard-card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0, fontSize: 18, fontWeight: 600 }}>Daily Budget Limit</h3>
            {editing !== 'daily' ? (
              <button
                className="btn btn-secondary"
                onClick={() => setEditing('daily')}
                style={{ padding: '6px 12px', fontSize: 14 }}
              >
                {orgBudget?.dailyLimitCredits ? 'Edit' : 'Set Limit'}
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="No limit"
                  defaultValue={orgBudget?.dailyLimitCredits || ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const value = e.target.value ? parseFloat(e.target.value) : null;
                      handleSaveOrgBudget('daily', value);
                    }
                    if (e.key === 'Escape') {
                      setEditing(null);
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: 14,
                    width: 120
                  }}
                  autoFocus
                />
                <button
                  className="btn btn-primary"
                  onClick={(e) => {
                    const value = e.target.previousSibling.value ? parseFloat(e.target.previousSibling.value) : null;
                    handleSaveOrgBudget('daily', value);
                  }}
                  disabled={saving}
                  style={{ padding: '6px 12px', fontSize: 14 }}
                >
                  Save
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setEditing(null)}
                  style={{ padding: '6px 12px', fontSize: 14 }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>
            {orgBudget?.dailyLimitCredits ? `Daily spending limit: $${orgBudget.dailyLimitCredits.toFixed(2)}` : 'No daily budget limit set'}
          </div>
          {orgBudget?.dailyLimitCredits && (
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Current Daily Spend: ${orgBudget.currentDailySpend.toFixed(2)}</div>
              <div style={{ width: '100%', height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${Math.min(100, getSpendPercentage(orgBudget.currentDailySpend, orgBudget.dailyLimitCredits))}%`,
                    height: '100%',
                    background: getSpendColor(getSpendPercentage(orgBudget.currentDailySpend, orgBudget.dailyLimitCredits)),
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 0, fontSize: 18, fontWeight: 600 }}>Monthly Budget Limit</h3>
            {editing !== 'monthly' ? (
              <button
                className="btn btn-secondary"
                onClick={() => setEditing('monthly')}
                style={{ padding: '6px 12px', fontSize: 14 }}
              >
                {orgBudget?.monthlyLimitCredits ? 'Edit' : 'Set Limit'}
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="No limit"
                  defaultValue={orgBudget?.monthlyLimitCredits || ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const value = e.target.value ? parseFloat(e.target.value) : null;
                      handleSaveOrgBudget('monthly', value);
                    }
                    if (e.key === 'Escape') {
                      setEditing(null);
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: 14,
                    width: 120
                  }}
                  autoFocus
                />
                <button
                  className="btn btn-primary"
                  onClick={(e) => {
                    const value = e.target.previousSibling.value ? parseFloat(e.target.previousSibling.value) : null;
                    handleSaveOrgBudget('monthly', value);
                  }}
                  disabled={saving}
                  style={{ padding: '6px 12px', fontSize: 14 }}
                >
                  Save
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setEditing(null)}
                  style={{ padding: '6px 12px', fontSize: 14 }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>
            {orgBudget?.monthlyLimitCredits ? `Monthly spending limit: $${orgBudget.monthlyLimitCredits.toFixed(2)}` : 'No monthly budget limit set'}
          </div>
          {orgBudget?.monthlyLimitCredits && (
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Current Monthly Spend: ${orgBudget.currentMonthlySpend.toFixed(2)}</div>
              <div style={{ width: '100%', height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${Math.min(100, getSpendPercentage(orgBudget.currentMonthlySpend, orgBudget.monthlyLimitCredits))}%`,
                    height: '100%',
                    background: getSpendColor(getSpendPercentage(orgBudget.currentMonthlySpend, orgBudget.monthlyLimitCredits)),
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Budget Alert Threshold</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                Alert when budget reaches (%)
              </label>
              {editing !== 'threshold' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={orgBudget?.alertThresholdPercent || 80}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 80;
                      handleSaveOrgBudget('threshold', value);
                    }}
                    style={{
                      width: '200px',
                      padding: '10px 12px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEditing('threshold')}
                    style={{ padding: '6px 12px', fontSize: 14 }}
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    defaultValue={orgBudget?.alertThresholdPercent || 80}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const value = parseInt(e.target.value) || 80;
                        handleSaveOrgBudget('threshold', value);
                      }
                      if (e.key === 'Escape') {
                        setEditing(null);
                      }
                    }}
                    style={{
                      width: '200px',
                      padding: '10px 12px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    autoFocus
                  />
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      const value = parseInt(e.target.previousSibling.value) || 80;
                      handleSaveOrgBudget('threshold', value);
                    }}
                    disabled={saving}
                    style={{ padding: '6px 12px', fontSize: 14 }}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEditing(null)}
                    style={{ padding: '6px 12px', fontSize: 14 }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                You will receive alerts when spending reaches {orgBudget?.alertThresholdPercent || 80}% of your budget limits
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', margin: 0 }}>User Budget Controls</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {selectedUsers.size > 0 && (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setBulkType('daily'); setShowBulkModal(true); }}
                  disabled={saving}
                  style={{ padding: '6px 12px', fontSize: 14 }}
                >
                  Set Bulk Daily Limit ({selectedUsers.size})
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setBulkType('monthly'); setShowBulkModal(true); }}
                  disabled={saving}
                  style={{ padding: '6px 12px', fontSize: 14 }}
                >
                  Set Bulk Monthly Limit ({selectedUsers.size})
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleBulkClear}
                  disabled={saving}
                  style={{ padding: '6px 12px', fontSize: 14 }}
                >
                  Clear All Limits ({selectedUsers.size})
                </button>
              </>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => {
                const csv = [
                  ['User Name', 'Email', 'Daily Limit', 'Monthly Limit', 'Daily Spend', 'Monthly Spend', 'Status'].join(','),
                  ...filteredUsers.map(u => [
                    `"${u.userName || ''}"`,
                    `"${u.userEmail || ''}"`,
                    u.dailyLimitCredits ?? 'Inherited',
                    u.monthlyLimitCredits ?? 'Inherited',
                    u.currentDailySpend.toFixed(2),
                    u.currentMonthlySpend.toFixed(2),
                    u.status
                  ].join(','))
                ].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `budget-controls-${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ padding: '6px 12px', fontSize: 14 }}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: 14
            }}
          />
        </div>

        <div className="dashboard-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#111827' }}>
                  <input
                    type="checkbox"
                    checked={filteredUsers.length > 0 && filteredUsers.every(u => selectedUsers.has(u.userId))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(new Set(filteredUsers.map(u => u.userId)));
                      } else {
                        setSelectedUsers(new Set());
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#111827' }}>User Name</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#111827' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#111827' }}>Daily Limit</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#111827' }}>Daily Spend</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#111827' }}>Monthly Limit</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#111827' }}>Monthly Spend</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#111827' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#111827' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                    {loading ? 'Loading...' : searchTerm ? 'No users found matching search' : 'No users in organization'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const dailyPct = getSpendPercentage(user.currentDailySpend, user.dailyLimitCredits);
                  const monthlyPct = getSpendPercentage(user.currentMonthlySpend, user.monthlyLimitCredits);
                  const isSelected = selectedUsers.has(user.userId);
                  
                  return (
                    <tr 
                      key={user.userId} 
                      style={{ 
                        borderBottom: '1px solid #f3f4f6',
                        background: user.status === 'AtLimit' ? '#fef2f2' : isSelected ? '#f0f9ff' : 'white',
                        opacity: user.status === 'AtLimit' ? 0.8 : 1
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const newSet = new Set(selectedUsers);
                            if (e.target.checked) {
                              newSet.add(user.userId);
                            } else {
                              newSet.delete(user.userId);
                            }
                            setSelectedUsers(newSet);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '12px', fontSize: 14, color: '#111827' }}>
                        {user.userName || 'N/A'}
                        {user.isInherited && (
                          <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6, fontStyle: 'italic' }}>(Inherited)</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: 14, color: '#111827' }}>{user.userEmail || 'N/A'}</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: 14, color: '#111827' }}>
                        {user.dailyLimitCredits != null ? `$${user.dailyLimitCredits.toFixed(2)}` : <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Inherited</span>}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: 14 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          <span style={{ color: '#111827' }}>${user.currentDailySpend.toFixed(2)}</span>
                          {user.dailyLimitCredits != null && (
                            <div style={{ width: 80, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                              <div
                                style={{
                                  width: `${Math.min(100, dailyPct)}%`,
                                  height: '100%',
                                  background: getSpendColor(dailyPct)
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: 14, color: '#111827' }}>
                        {user.monthlyLimitCredits != null ? `$${user.monthlyLimitCredits.toFixed(2)}` : <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Inherited</span>}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontSize: 14 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          <span style={{ color: '#111827' }}>${user.currentMonthlySpend.toFixed(2)}</span>
                          {user.monthlyLimitCredits != null && (
                            <div style={{ width: 80, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                              <div
                                style={{
                                  width: `${Math.min(100, monthlyPct)}%`,
                                  height: '100%',
                                  background: getSpendColor(monthlyPct)
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: 12,
                          fontWeight: 500,
                          background: user.status === 'AtLimit' ? '#fee2e2' : '#d1fae5',
                          color: user.status === 'AtLimit' ? '#991b1b' : '#065f46'
                        }}>
                          {user.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          <button
                            className="btn btn-secondary"
                            onClick={() => setEditingUser(user.userId)}
                            disabled={saving}
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            title="Edit Budget"
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleResetUserSpend(user.userId)}
                            disabled={saving}
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            title="Reset Spend"
                          >
                            Reset
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingUser && (() => {
        const user = userBudgets.find(u => u.userId === editingUser);
        if (!user) return null;
        
        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={() => setEditingUser(null)}>
            <div className="dashboard-card" style={{
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: 24
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Edit Budget for {user.userName || user.userEmail}</h3>
                <button onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Daily Budget Limit ($)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="No limit (inherit from organization)"
                      defaultValue={user.dailyLimitCredits || ''}
                      id="edit-daily-limit"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1.5px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: 14
                      }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        id="edit-daily-no-limit"
                        defaultChecked={!user.dailyLimitCredits}
                        onChange={(e) => {
                          const input = document.getElementById('edit-daily-limit');
                          if (input) input.disabled = e.target.checked;
                        }}
                      />
                      <span style={{ fontSize: 13, color: '#6b7280' }}>No limit</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Monthly Budget Limit ($)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="No limit (inherit from organization)"
                      defaultValue={user.monthlyLimitCredits || ''}
                      id="edit-monthly-limit"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1.5px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: 14
                      }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        id="edit-monthly-no-limit"
                        defaultChecked={!user.monthlyLimitCredits}
                        onChange={(e) => {
                          const input = document.getElementById('edit-monthly-limit');
                          if (input) input.disabled = e.target.checked;
                        }}
                      />
                      <span style={{ fontSize: 13, color: '#6b7280' }}>No limit</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Alert Threshold (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Use organization default"
                    defaultValue={user.alertThresholdPercent || ''}
                    id="edit-alert-threshold"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: 14
                    }}
                  />
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Leave empty to use organization default ({orgBudget?.alertThresholdPercent || 80}%)
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      defaultChecked={user.overrideOrgLimits}
                      id="edit-override-org"
                    />
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Override Organization Limits</span>
                  </label>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginLeft: 24 }}>
                    When enabled, user limits can exceed organization limits
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    placeholder="Add notes about why limits were set..."
                    defaultValue={user.adminNotes || ''}
                    id="edit-admin-notes"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: 14,
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEditingUser(null)}
                    style={{ padding: '8px 16px', fontSize: 14 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      const dailyNoLimit = document.getElementById('edit-daily-no-limit')?.checked;
                      const monthlyNoLimit = document.getElementById('edit-monthly-no-limit')?.checked;
                      const dailyLimit = dailyNoLimit ? null : (document.getElementById('edit-daily-limit')?.value ? parseFloat(document.getElementById('edit-daily-limit').value) : null);
                      const monthlyLimit = monthlyNoLimit ? null : (document.getElementById('edit-monthly-limit')?.value ? parseFloat(document.getElementById('edit-monthly-limit').value) : null);
                      const alertThreshold = document.getElementById('edit-alert-threshold')?.value ? parseInt(document.getElementById('edit-alert-threshold').value) : null;
                      const overrideOrgLimits = document.getElementById('edit-override-org')?.checked || false;
                      const adminNotes = document.getElementById('edit-admin-notes')?.value || null;

                      handleSaveUserBudget(editingUser, {
                        dailyLimitCredits: dailyLimit,
                        monthlyLimitCredits: monthlyLimit,
                        alertThresholdPercent: alertThreshold,
                        overrideOrgLimits: overrideOrgLimits,
                        adminNotes: adminNotes
                      });
                    }}
                    disabled={saving}
                    style={{ padding: '8px 16px', fontSize: 14 }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      if (window.confirm('Remove budget limits for this user? They will inherit organization defaults.')) {
                        handleDeleteUserBudget(editingUser);
                      }
                    }}
                    disabled={saving}
                    style={{ padding: '8px 16px', fontSize: 14, background: '#dc2626', color: 'white' }}
                  >
                    Remove Limits
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showBulkModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowBulkModal(false)}>
          <div className="dashboard-card" style={{
            maxWidth: '500px',
            width: '90%',
            padding: 24
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Set Bulk {bulkType === 'daily' ? 'Daily' : 'Monthly'} Limit</h3>
              <button onClick={() => setShowBulkModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
                This will set the {bulkType} limit to the same value for {selectedUsers.size} selected users.
              </p>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                {bulkType === 'daily' ? 'Daily' : 'Monthly'} Budget Limit ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter amount"
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: 14
                }}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowBulkModal(false); setBulkValue(''); }}
                style={{ padding: '8px 16px', fontSize: 14 }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkSet}
                disabled={saving || !bulkValue || parseFloat(bulkValue) <= 0}
                style={{ padding: '8px 16px', fontSize: 14 }}
              >
                {saving ? 'Setting...' : `Set for ${selectedUsers.size} Users`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Real-Time Dashboard Wrapper (for Enterprise)
function RealTimeDashboardWrapper({ orgId }) {
  const [userRole, setUserRole] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      if (!orgId) {
        setLoading(false);
        return;
      }
      try {
        // Get user info for role
        const user = await authService.getUserInfo();
        const isAdmin = user?.roles?.some(r => r.toLowerCase() === 'admin') || false;
        setUserRole(isAdmin ? 'Admin' : 'EnterpriseAdmin');
      } catch (err) {
        console.error('Failed to load user role:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;
  }

  return <RealTimeDashboard userRole={userRole} organizationId={orgId} />;
}

// Real-Time Monitoring Component (legacy - kept for compatibility)
// eslint-disable-next-line no-unused-vars
function RealTimeMonitoring({ orgId }) {
  const [metrics, setMetrics] = React.useState({ activeExecutions: 0, requestsLastMinute: 0, avgResponseTime: 0 });
  const [updating, setUpdating] = React.useState(false);

  React.useEffect(() => {
    if (!orgId) return;
    const interval = setInterval(() => {
      loadRealTimeMetrics();
    }, 5000); // Update every 5 seconds
    loadRealTimeMetrics();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadRealTimeMetrics = async () => {
    if (!orgId) return;
    setUpdating(true);
    try {
      const t = authService.getToken();
      // TODO: Create backend endpoint GET /api/orgs/{id}/monitoring/real-time
      const res = await fetch(`/api/orgs/${orgId}/logs/executions?status=running&pageSize=100`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setMetrics({
          activeExecutions: data.total || 0,
          requestsLastMinute: 0,
          avgResponseTime: 0
        });
      }
    } catch (e) {
      console.error('Failed to load real-time metrics:', e);
    } finally {
      setUpdating(false);
    }
  };

  if (!orgId) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Please select an organization</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 0 }}>Real-Time Monitoring</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          {updating ? 'Updating...' : 'Auto-refresh: 5s'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        <div className="dashboard-card" style={{ padding: 16 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Active Executions</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#111827' }}>
            {metrics.activeExecutions}
          </div>
        </div>
        <div className="dashboard-card" style={{ padding: 16 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Requests (Last Minute)</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#111827' }}>
            {metrics.requestsLastMinute}
          </div>
        </div>
        <div className="dashboard-card" style={{ padding: 16 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Avg Response Time</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: '#111827' }}>
            {metrics.avgResponseTime}ms
          </div>
        </div>
      </div>

      <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          <strong>Note:</strong> Real-time monitoring updates every 5 seconds. Backend endpoints for detailed metrics are needed for full functionality.
        </div>
      </div>
    </div>
  );
}

// Performance Monitoring Component
function PerformanceMonitoring({ orgId }) {
  const [loading, setLoading] = React.useState(false);
  const [performanceData, setPerformanceData] = React.useState(null);
  const [period, setPeriod] = React.useState('24h');

  React.useEffect(() => {
    if (orgId) {
      loadPerformanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, period]);

  const loadPerformanceData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/orgs/${orgId}/logs/api?pageSize=100`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        const items = data.items || [];
        const responseTimes = items.filter(l => l.responseTimeMs).map(l => l.responseTimeMs);
        const avgResponseTime = responseTimes.length > 0 
          ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length 
          : 0;
        const p95ResponseTime = responseTimes.length > 0
          ? responseTimes.sort((a, b) => a - b)[Math.floor(responseTimes.length * 0.95)]
          : 0;
        
        setPerformanceData({
          avgResponseTime,
          p95ResponseTime,
          totalRequests: items.length,
          errorRate: items.filter(l => l.statusCode >= 400).length / Math.max(1, items.length) * 100
        });
      }
    } catch (e) {
      console.error('Failed to load performance data:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!orgId) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Please select an organization</div>;
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading performance data...</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 0 }}>Performance Monitoring</h2>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1.5px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            background: '#fff',
            cursor: 'pointer'
          }}
        >
          <option value="1h">Last Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>

      {performanceData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div className="dashboard-card" style={{ padding: 16 }}>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Avg Response Time</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>
                {performanceData.avgResponseTime.toFixed(0)}ms
              </div>
            </div>
            <div className="dashboard-card" style={{ padding: 16 }}>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>P95 Response Time</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>
                {performanceData.p95ResponseTime.toFixed(0)}ms
              </div>
            </div>
            <div className="dashboard-card" style={{ padding: 16 }}>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Total Requests</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#111827' }}>
                {performanceData.totalRequests.toLocaleString()}
              </div>
            </div>
            <div className="dashboard-card" style={{ padding: 16 }}>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Error Rate</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: performanceData.errorRate > 5 ? '#dc2626' : '#111827' }}>
                {performanceData.errorRate.toFixed(2)}%
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// User Analytics Component
function UserAnalytics({ orgId }) {
  const [loading, setLoading] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(null);

  React.useEffect(() => {
    if (orgId) {
      loadUserAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadUserAnalytics = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/orgs/${orgId}/usage/top-consumers?type=users&limit=20`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (e) {
      console.error('Failed to load user analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!orgId) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Please select an organization</div>;
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading user analytics...</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 12 }}>User Analytics</h2>
      
      {analytics && analytics.length > 0 ? (
        <>
          <div className="dashboard-card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Top Users by Usage</h3>
            <HorizontalBarChart
              items={analytics.map(u => ({
                label: u.email || u.userId || 'Unknown',
                value: u.requestCount || 0
              }))}
              height={Math.min(400, analytics.length * 40)}
              color="#667eea"
            />
          </div>
        </>
      ) : (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No user analytics data available</div>
      )}
    </div>
  );
}

// Script Analytics Component
function ScriptAnalytics({ orgId }) {
  const [loading, setLoading] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(null);

  React.useEffect(() => {
    if (orgId) {
      loadScriptAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadScriptAnalytics = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const t = authService.getToken();
      const res = await fetch(`/api/orgs/${orgId}/logs/executions?pageSize=100`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        const scriptCounts = {};
        (data.items || []).forEach(exec => {
          const scriptName = exec.scriptName || 'Ad-hoc';
          scriptCounts[scriptName] = (scriptCounts[scriptName] || 0) + 1;
        });
        setAnalytics(Object.entries(scriptCounts).map(([name, count]) => ({ name, count })));
      }
    } catch (e) {
      console.error('Failed to load script analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!orgId) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Please select an organization</div>;
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading script analytics...</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 12 }}>Script Analytics</h2>
      
      {analytics && analytics.length > 0 ? (
        <>
          <div className="dashboard-card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 600 }}>Most Used Scripts</h3>
            <HorizontalBarChart
              items={analytics.map(s => ({
                label: s.name,
                value: s.count
              }))}
              height={Math.min(400, analytics.length * 40)}
              color="#059669"
            />
          </div>
        </>
      ) : (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No script analytics data available</div>
      )}
    </div>
  );
}

// Custom Reports Component
function CustomReports({ orgId }) {
  const [reports] = React.useState([]);
  const [showCreateReport, setShowCreateReport] = React.useState(false);

  React.useEffect(() => {
    if (orgId) {
      loadReports();
    }
  }, [orgId]);

  const loadReports = async () => {
    // TODO: Create backend endpoint GET /api/orgs/{id}/reports
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 className="admin-header" style={{ marginTop: 0, marginBottom: 0 }}>Custom Reports</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateReport(true)}
          style={{ padding: '8px 16px', fontSize: 14 }}
        >
          Create Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="dashboard-card" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          No custom reports created yet. Create your first report to get started.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {reports.map((report, idx) => (
            <div key={idx} className="dashboard-card" style={{ padding: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{report.name}</div>
              <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>{report.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EnterprisePolicy = () => {
  const [orgs, setOrgs] = React.useState([]);
  const [orgId, setOrgId] = React.useState(null);
  const [tab, setTab] = React.useState('overview'); // overview | users-and-access | moderation | billing | monitoring | script-governance
  const [usersAccessSubTab, setUsersAccessSubTab] = React.useState('user-directory'); // user-directory | roles-permissions | authentication | teams-groups | access-logs
  const [billingSubTab, setBillingSubTab] = React.useState('dashboard'); // dashboard | tokens | analytics | settings | invoices | budget
  const [creditManagementView, setCreditManagementView] = React.useState('balance'); // balance | history | expiration | auto-recharge | subscription
  const [monitoringSubTab, setMonitoringSubTab] = React.useState('system-logs'); // system-logs | real-time | performance | alerting | user-analytics | script-analytics | custom-reports
  const [systemLogsView, setSystemLogsView] = React.useState('execution'); // execution | error | api | system-event | export
  const [members, setMembers] = React.useState([]);
  const [busyMembers, setBusyMembers] = React.useState(false);
  const [showAllUsers, setShowAllUsers] = React.useState(false);
  const [addUserId, setAddUserId] = React.useState('');
  const [addRole, setAddRole] = React.useState('member');
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteFirst, setInviteFirst] = React.useState('');
  const [inviteLast, setInviteLast] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('member');
  const [sendEmail, setSendEmail] = React.useState(true);
  const [inviteLink, setInviteLink] = React.useState('');
  const [inviteMsg, setInviteMsg] = React.useState('');
  const [csvBusy, setCsvBusy] = React.useState(false);
  const [csvLog, setCsvLog] = React.useState([]);
  const fileInputRef = React.useRef(null);
  const [modSettings, setModSettings] = React.useState({
    hateWarn: 0.35, hateBlock: 0.75,
    violenceWarn: 0.35, violenceBlock: 0.75,
    selfHarmWarn: 0.35, selfHarmBlock: 0.75,
    blockPci: true, blockCsam: true, forceDoNotStoreOnPiiWarn: false
  });
  const [savingMods, setSavingMods] = React.useState(false);
  const [modSaveMsg, setModSaveMsg] = React.useState('');
  const [copiedIds, setCopiedIds] = React.useState({});
  const [stats, setStats] = React.useState({ allUsers: 0, members: 0, admins: 0, owners: 0 });
  const [loadingStats, setLoadingStats] = React.useState(false);
  const [allowScriptSharing, setAllowScriptSharing] = React.useState(true);
  const [savingScriptSharing, setSavingScriptSharing] = React.useState(false);
  
  // Credit Management state
  const [creditBalance, setCreditBalance] = React.useState(null);
  const [loadingCreditBalance, setLoadingCreditBalance] = React.useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_expandedTeams, _setExpandedTeams] = React.useState({});
  const [showPurchaseModal, setShowPurchaseModal] = React.useState(false);
  const [showAllocationModal, setShowAllocationModal] = React.useState(false);
  const [availableTeams, setAvailableTeams] = React.useState([]);
  const [availableUsers, setAvailableUsers] = React.useState([]);
  
  // Execution Logs state
  const [executionLogs, setExecutionLogs] = React.useState([]);
  const [loadingExecutionLogs, setLoadingExecutionLogs] = React.useState(false);
  const [executionLogsFilters, setExecutionLogsFilters] = React.useState({
    startDate: null,
    endDate: null,
    userId: '',
    scriptName: '',
    status: []
  });
  const [executionLogsSearch, setExecutionLogsSearch] = React.useState('');
  const [executionLogsSort, setExecutionLogsSort] = React.useState({ field: 'startedAt', direction: 'desc' });
  const [executionLogsPage, setExecutionLogsPage] = React.useState(1);
  // eslint-disable-next-line no-unused-vars
  const [executionLogsPageSize, _setExecutionLogsPageSize] = React.useState(50);
  const [executionLogsTotal, setExecutionLogsTotal] = React.useState(0);
  const [selectedExecutionLog, setSelectedExecutionLog] = React.useState(null);
  const [orgMembers, setOrgMembers] = React.useState([]);
  
  // Error Logs state
  const [errorLogs, setErrorLogs] = React.useState([]);
  const [loadingErrorLogs, setLoadingErrorLogs] = React.useState(false);
  const [errorLogsFilters, setErrorLogsFilters] = React.useState({
    startDate: null,
    endDate: null,
    severity: [],
    errorType: [],
    userId: '',
    scriptName: '',
    aiModel: '',
    resolutionStatus: '',
    component: '',
    minOccurrenceCount: null,
    maxOccurrenceCount: null
  });
  const [errorLogsSearch, setErrorLogsSearch] = React.useState('');
  // eslint-disable-next-line no-unused-vars
  const [errorLogsSort, _setErrorLogsSort] = React.useState({ field: 'timestamp', direction: 'desc' });
  const [errorLogsPage, setErrorLogsPage] = React.useState(1);
  // eslint-disable-next-line no-unused-vars
  const [errorLogsPageSize, _setErrorLogsPageSize] = React.useState(50);
  const [errorLogsTotal, setErrorLogsTotal] = React.useState(0);
  const [selectedErrorLog, setSelectedErrorLog] = React.useState(null);
  
  // API Logs state
  const [apiLogs, setApiLogs] = React.useState([]);
  const [loadingApiLogs, setLoadingApiLogs] = React.useState(false);
  const [apiLogsFilters, setApiLogsFilters] = React.useState({
    startDate: null,
    endDate: null,
    direction: '',
    httpMethod: '',
    endpoint: '',
    statusCode: null,
    minResponseTime: null,
    maxResponseTime: null,
    userId: '',
    externalProvider: '',
    errorOccurred: null
  });
  const [apiLogsSearch, setApiLogsSearch] = React.useState('');
  // eslint-disable-next-line no-unused-vars
  const [apiLogsSort, _setApiLogsSort] = React.useState({ field: 'timestamp', direction: 'desc' });
  const [apiLogsPage, setApiLogsPage] = React.useState(1);
  // eslint-disable-next-line no-unused-vars
  const [apiLogsPageSize, _setApiLogsPageSize] = React.useState(50);
  const [apiLogsTotal, setApiLogsTotal] = React.useState(0);
  const [selectedApiLog, setSelectedApiLog] = React.useState(null);
  
  // System Event Logs state
  const [systemEventLogs, setSystemEventLogs] = React.useState([]);
  const [loadingSystemEventLogs, setLoadingSystemEventLogs] = React.useState(false);
  const [systemEventLogsFilters, setSystemEventLogsFilters] = React.useState({
    startDate: null,
    endDate: null,
    eventType: [],
    eventCategory: [],
    severity: '',
    actorId: '',
    targetEntityType: '',
    action: '',
    result: '',
    complianceRelevant: null,
    securitySensitive: null
  });
  const [systemEventLogsSearch, setSystemEventLogsSearch] = React.useState('');
  // eslint-disable-next-line no-unused-vars
  const [systemEventLogsSort, _setSystemEventLogsSort] = React.useState({ field: 'timestamp', direction: 'desc' });
  const [systemEventLogsPage, setSystemEventLogsPage] = React.useState(1);
  // eslint-disable-next-line no-unused-vars
  const [systemEventLogsPageSize, _setSystemEventLogsPageSize] = React.useState(50);
  const [systemEventLogsTotal, setSystemEventLogsTotal] = React.useState(0);
  const [selectedSystemEventLog, setSelectedSystemEventLog] = React.useState(null);
  
  // Export Logs state
  const [exportLogs, setExportLogs] = React.useState([]);
  const [loadingExportLogs, setLoadingExportLogs] = React.useState(false);
  const [exportLogsFilters, setExportLogsFilters] = React.useState({
    startDate: null,
    endDate: null,
    exportType: [],
    exportFormat: '',
    userId: '',
    dataClassification: '',
    containsPii: null,
    status: '',
    reason: '',
    minFileSize: null,
    maxFileSize: null,
    minRecordCount: null,
    maxRecordCount: null
  });
  const [exportLogsSearch, setExportLogsSearch] = React.useState('');
  // eslint-disable-next-line no-unused-vars
  const [exportLogsSort, _setExportLogsSort] = React.useState({ field: 'timestamp', direction: 'desc' });
  const [exportLogsPage, setExportLogsPage] = React.useState(1);
  // eslint-disable-next-line no-unused-vars
  const [exportLogsPageSize, _setExportLogsPageSize] = React.useState(50);
  const [exportLogsTotal, setExportLogsTotal] = React.useState(0);
  const [selectedExportLog, setSelectedExportLog] = React.useState(null);
  
  // Usage Dashboard state
  const [dashboardData, setDashboardData] = React.useState(null);
  const [trendsData, setTrendsData] = React.useState([]);
  const [trendsPeriod, setTrendsPeriod] = React.useState('30');
  const [trendsView, setTrendsView] = React.useState('requests'); // requests | cost | tokens
  const [topConsumersType, setTopConsumersType] = React.useState('users'); // users | models
  const [topConsumers, setTopConsumers] = React.useState([]);
  const [usageByTeam, setUsageByTeam] = React.useState([]);
  const [forecastData, setForecastData] = React.useState(null);
  const [balancesData, setBalancesData] = React.useState(null);
  const [loadingDashboard, setLoadingDashboard] = React.useState(false);
  const [isAdminOrOwner, setIsAdminOrOwner] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const t = authService.getToken();
        const r = await fetch('/api/orgs/mine', { headers: t ? { Authorization:`Bearer ${t}` } : {} });
        if (!r.ok) return;
        const items = await r.json();
        setOrgs(Array.isArray(items) ? items : []);
        if (Array.isArray(items) && items.length > 0) setOrgId(items[0].id);
      } catch {}
    })();
  }, []);

  const loadMembers = React.useCallback(async (id) => {
    if (!id) return;
    setBusyMembers(true);
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/members`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      const j = await r.json().catch(()=>[]);
      setMembers(Array.isArray(j) ? j : []);
    } catch { setMembers([]); }
    finally { setBusyMembers(false); }
  }, []);

  React.useEffect(() => { if (tab==='users-and-access' && usersAccessSubTab==='user-directory' && orgId) loadMembers(orgId); }, [tab, usersAccessSubTab, orgId, loadMembers]);

  const loadModSettings = React.useCallback(async (id) => {
    if (!id) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/moderation/settings`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const s = await r.json();
        setModSettings({
          hateWarn: Number(s.hateWarn || 0.35),
          hateBlock: Number(s.hateBlock || 0.75),
          violenceWarn: Number(s.violenceWarn || 0.35),
          violenceBlock: Number(s.violenceBlock || 0.75),
          selfHarmWarn: Number(s.selfHarmWarn || 0.35),
          selfHarmBlock: Number(s.selfHarmBlock || 0.75),
          blockPci: s.blockPci !== undefined ? s.blockPci : true,
          blockCsam: s.blockCsam !== undefined ? s.blockCsam : true,
          forceDoNotStoreOnPiiWarn: s.forceDoNotStoreOnPiiWarn || false
        });
      }
    } catch {}
  }, []);

  React.useEffect(() => { if (tab==='moderation' && orgId) loadModSettings(orgId); }, [tab, orgId, loadModSettings]);

  const loadStats = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingStats(true);
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/stats`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const s = await r.json();
        setStats({
          allUsers: s.allUsers || 0,
          members: s.members || 0,
          admins: s.admins || 0,
          owners: s.owners || 0
        });
      }
    } catch {}
    finally { setLoadingStats(false); }
  }, []);

  React.useEffect(() => { if (tab==='overview' && orgId) loadStats(orgId); }, [tab, orgId, loadStats]);

  const loadOrgDetails = React.useCallback(async (id) => {
    if (!id) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const org = await r.json();
        setAllowScriptSharing(org.allowScriptSharing !== undefined ? org.allowScriptSharing : true);
      }
    } catch {}
  }, []);

  React.useEffect(() => { if (tab==='overview' && orgId) loadOrgDetails(orgId); }, [tab, orgId, loadOrgDetails]);

  // Load org members for execution logs filter
  const loadOrgMembersForLogs = React.useCallback(async (id) => {
    if (!id) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/members`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const j = await r.json();
        setOrgMembers(Array.isArray(j) ? j : []);
      }
    } catch {}
  }, []);

  // Load execution logs
  const loadExecutionLogs = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingExecutionLogs(true);
    try {
      const t = authService.getToken();
      const params = new URLSearchParams();
      if (executionLogsFilters.startDate) params.append('startDate', executionLogsFilters.startDate.toISOString());
      if (executionLogsFilters.endDate) params.append('endDate', executionLogsFilters.endDate.toISOString());
      if (executionLogsFilters.userId) params.append('userId', executionLogsFilters.userId);
      if (executionLogsFilters.scriptName) params.append('scriptName', executionLogsFilters.scriptName);
      if (executionLogsFilters.status.length > 0) params.append('status', executionLogsFilters.status.join(','));
      if (executionLogsSearch) params.append('search', executionLogsSearch);
      params.append('sortBy', executionLogsSort.field);
      params.append('sortDir', executionLogsSort.direction);
      params.append('page', executionLogsPage.toString());
      params.append('pageSize', executionLogsPageSize.toString());
      
      const r = await fetch(`/api/orgs/${id}/logs/executions?${params.toString()}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setExecutionLogs(Array.isArray(data.items) ? data.items : []);
        setExecutionLogsTotal(data.total || 0);
      }
    } catch {}
    finally { setLoadingExecutionLogs(false); }
  }, [executionLogsFilters, executionLogsSearch, executionLogsSort, executionLogsPage, executionLogsPageSize]);

  // Load execution log detail
  const loadExecutionLogDetail = React.useCallback(async (id, executionId) => {
    if (!id || !executionId) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/logs/executions/${executionId}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setSelectedExecutionLog(data);
      }
    } catch {}
  }, []);

  // Load error logs
  const loadErrorLogs = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingErrorLogs(true);
    try {
      const t = authService.getToken();
      const params = new URLSearchParams();
      if (errorLogsFilters.startDate) params.append('startDate', errorLogsFilters.startDate.toISOString());
      if (errorLogsFilters.endDate) params.append('endDate', errorLogsFilters.endDate.toISOString());
      if (errorLogsFilters.severity.length > 0) params.append('severity', errorLogsFilters.severity.join(','));
      if (errorLogsFilters.errorType.length > 0) params.append('errorType', errorLogsFilters.errorType.join(','));
      if (errorLogsFilters.userId) params.append('userId', errorLogsFilters.userId);
      if (errorLogsFilters.scriptName) params.append('scriptName', errorLogsFilters.scriptName);
      if (errorLogsFilters.aiModel) params.append('aiModel', errorLogsFilters.aiModel);
      if (errorLogsFilters.resolutionStatus) params.append('resolutionStatus', errorLogsFilters.resolutionStatus);
      if (errorLogsFilters.component) params.append('component', errorLogsFilters.component);
      if (errorLogsFilters.minOccurrenceCount !== null) params.append('minOccurrenceCount', errorLogsFilters.minOccurrenceCount.toString());
      if (errorLogsFilters.maxOccurrenceCount !== null) params.append('maxOccurrenceCount', errorLogsFilters.maxOccurrenceCount.toString());
      if (errorLogsSearch) params.append('search', errorLogsSearch);
      params.append('sortBy', errorLogsSort.field);
      params.append('sortDir', errorLogsSort.direction);
      params.append('page', errorLogsPage.toString());
      params.append('pageSize', errorLogsPageSize.toString());
      
      const r = await fetch(`/api/orgs/${id}/logs/errors?${params.toString()}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setErrorLogs(Array.isArray(data.items) ? data.items : []);
        setErrorLogsTotal(data.total || 0);
      }
    } catch {}
    finally { setLoadingErrorLogs(false); }
  }, [errorLogsFilters, errorLogsSearch, errorLogsSort, errorLogsPage, errorLogsPageSize]);

  const loadErrorLogDetail = React.useCallback(async (id, errorId) => {
    if (!id || !errorId) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/logs/errors/${errorId}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setSelectedErrorLog(data);
      }
    } catch {}
  }, []);

  // Load API logs
  const loadApiLogs = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingApiLogs(true);
    try {
      const t = authService.getToken();
      const params = new URLSearchParams();
      if (apiLogsFilters.startDate) params.append('startDate', apiLogsFilters.startDate.toISOString());
      if (apiLogsFilters.endDate) params.append('endDate', apiLogsFilters.endDate.toISOString());
      if (apiLogsFilters.direction) params.append('direction', apiLogsFilters.direction);
      if (apiLogsFilters.httpMethod) params.append('httpMethod', apiLogsFilters.httpMethod);
      if (apiLogsFilters.endpoint) params.append('endpoint', apiLogsFilters.endpoint);
      if (apiLogsFilters.statusCode !== null) params.append('statusCode', apiLogsFilters.statusCode.toString());
      if (apiLogsFilters.minResponseTime !== null) params.append('minResponseTime', apiLogsFilters.minResponseTime.toString());
      if (apiLogsFilters.maxResponseTime !== null) params.append('maxResponseTime', apiLogsFilters.maxResponseTime.toString());
      if (apiLogsFilters.userId) params.append('userId', apiLogsFilters.userId);
      if (apiLogsFilters.externalProvider) params.append('externalProvider', apiLogsFilters.externalProvider);
      if (apiLogsFilters.errorOccurred !== null) params.append('errorOccurred', apiLogsFilters.errorOccurred.toString());
      if (apiLogsSearch) params.append('search', apiLogsSearch);
      params.append('sortBy', apiLogsSort.field);
      params.append('sortDir', apiLogsSort.direction);
      params.append('page', apiLogsPage.toString());
      params.append('pageSize', apiLogsPageSize.toString());
      
      const r = await fetch(`/api/orgs/${id}/logs/api?${params.toString()}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setApiLogs(Array.isArray(data.items) ? data.items : []);
        setApiLogsTotal(data.total || 0);
      }
    } catch {}
    finally { setLoadingApiLogs(false); }
  }, [apiLogsFilters, apiLogsSearch, apiLogsSort, apiLogsPage, apiLogsPageSize]);

  const loadApiLogDetail = React.useCallback(async (id, apiLogId) => {
    if (!id || !apiLogId) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/logs/api/${apiLogId}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setSelectedApiLog(data);
      }
    } catch {}
  }, []);

  // Load system event logs
  const loadSystemEventLogs = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingSystemEventLogs(true);
    try {
      const t = authService.getToken();
      const params = new URLSearchParams();
      if (systemEventLogsFilters.startDate) params.append('startDate', systemEventLogsFilters.startDate.toISOString());
      if (systemEventLogsFilters.endDate) params.append('endDate', systemEventLogsFilters.endDate.toISOString());
      if (systemEventLogsFilters.eventType.length > 0) params.append('eventType', systemEventLogsFilters.eventType.join(','));
      if (systemEventLogsFilters.eventCategory.length > 0) params.append('eventCategory', systemEventLogsFilters.eventCategory.join(','));
      if (systemEventLogsFilters.severity) params.append('severity', systemEventLogsFilters.severity);
      if (systemEventLogsFilters.actorId) params.append('actorId', systemEventLogsFilters.actorId);
      if (systemEventLogsFilters.targetEntityType) params.append('targetEntityType', systemEventLogsFilters.targetEntityType);
      if (systemEventLogsFilters.action) params.append('action', systemEventLogsFilters.action);
      if (systemEventLogsFilters.result) params.append('result', systemEventLogsFilters.result);
      if (systemEventLogsFilters.complianceRelevant !== null) params.append('complianceRelevant', systemEventLogsFilters.complianceRelevant.toString());
      if (systemEventLogsFilters.securitySensitive !== null) params.append('securitySensitive', systemEventLogsFilters.securitySensitive.toString());
      if (systemEventLogsSearch) params.append('search', systemEventLogsSearch);
      params.append('sortBy', systemEventLogsSort.field);
      params.append('sortDir', systemEventLogsSort.direction);
      params.append('page', systemEventLogsPage.toString());
      params.append('pageSize', systemEventLogsPageSize.toString());
      
      const r = await fetch(`/api/orgs/${id}/logs/system-events?${params.toString()}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setSystemEventLogs(Array.isArray(data.items) ? data.items : []);
        setSystemEventLogsTotal(data.total || 0);
      }
    } catch {}
    finally { setLoadingSystemEventLogs(false); }
  }, [systemEventLogsFilters, systemEventLogsSearch, systemEventLogsSort, systemEventLogsPage, systemEventLogsPageSize]);

  const loadSystemEventLogDetail = React.useCallback(async (id, eventId) => {
    if (!id || !eventId) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/logs/system-events/${eventId}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setSelectedSystemEventLog(data);
      }
    } catch {}
  }, []);

  // Load export logs
  const loadExportLogs = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingExportLogs(true);
    try {
      const t = authService.getToken();
      const params = new URLSearchParams();
      if (exportLogsFilters.startDate) params.append('startDate', exportLogsFilters.startDate.toISOString());
      if (exportLogsFilters.endDate) params.append('endDate', exportLogsFilters.endDate.toISOString());
      if (exportLogsFilters.exportType.length > 0) params.append('exportType', exportLogsFilters.exportType.join(','));
      if (exportLogsFilters.exportFormat) params.append('exportFormat', exportLogsFilters.exportFormat);
      if (exportLogsFilters.userId) params.append('userId', exportLogsFilters.userId);
      if (exportLogsFilters.dataClassification) params.append('dataClassification', exportLogsFilters.dataClassification);
      if (exportLogsFilters.containsPii !== null) params.append('containsPii', exportLogsFilters.containsPii.toString());
      if (exportLogsFilters.status) params.append('status', exportLogsFilters.status);
      if (exportLogsFilters.reason) params.append('reason', exportLogsFilters.reason);
      if (exportLogsFilters.minFileSize !== null) params.append('minFileSize', exportLogsFilters.minFileSize.toString());
      if (exportLogsFilters.maxFileSize !== null) params.append('maxFileSize', exportLogsFilters.maxFileSize.toString());
      if (exportLogsFilters.minRecordCount !== null) params.append('minRecordCount', exportLogsFilters.minRecordCount.toString());
      if (exportLogsFilters.maxRecordCount !== null) params.append('maxRecordCount', exportLogsFilters.maxRecordCount.toString());
      if (exportLogsSearch) params.append('search', exportLogsSearch);
      params.append('sortBy', exportLogsSort.field);
      params.append('sortDir', exportLogsSort.direction);
      params.append('page', exportLogsPage.toString());
      params.append('pageSize', exportLogsPageSize.toString());
      
      const r = await fetch(`/api/orgs/${id}/logs/exports?${params.toString()}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setExportLogs(Array.isArray(data.items) ? data.items : []);
        setExportLogsTotal(data.total || 0);
      }
    } catch {}
    finally { setLoadingExportLogs(false); }
  }, [exportLogsFilters, exportLogsSearch, exportLogsSort, exportLogsPage, exportLogsPageSize]);

  const loadExportLogDetail = React.useCallback(async (id, exportId) => {
    if (!id || !exportId) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/logs/exports/${exportId}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setSelectedExportLog(data);
      }
    } catch {}
  }, []);

  React.useEffect(() => {
    if (tab === 'monitoring' && monitoringSubTab === 'system-logs' && systemLogsView === 'execution' && orgId) {
      loadOrgMembersForLogs(orgId);
    }
  }, [tab, monitoringSubTab, systemLogsView, orgId, loadOrgMembersForLogs]);

  React.useEffect(() => {
    if (tab === 'monitoring' && monitoringSubTab === 'system-logs' && systemLogsView === 'execution' && orgId) {
      loadExecutionLogs(orgId);
    }
  }, [tab, monitoringSubTab, systemLogsView, orgId, executionLogsFilters, executionLogsSearch, executionLogsSort, executionLogsPage, executionLogsPageSize, loadExecutionLogs]);

  React.useEffect(() => {
    if (tab === 'monitoring' && monitoringSubTab === 'system-logs' && systemLogsView === 'error' && orgId) {
      loadOrgMembersForLogs(orgId);
      loadErrorLogs(orgId);
    }
  }, [tab, monitoringSubTab, systemLogsView, orgId, errorLogsFilters, errorLogsSearch, errorLogsSort, errorLogsPage, errorLogsPageSize, loadErrorLogs, loadOrgMembersForLogs]);

  React.useEffect(() => {
    if (tab === 'monitoring' && monitoringSubTab === 'system-logs' && systemLogsView === 'api' && orgId) {
      loadOrgMembersForLogs(orgId);
      loadApiLogs(orgId);
    }
  }, [tab, monitoringSubTab, systemLogsView, orgId, apiLogsFilters, apiLogsSearch, apiLogsSort, apiLogsPage, apiLogsPageSize, loadApiLogs, loadOrgMembersForLogs]);

  React.useEffect(() => {
    if (tab === 'monitoring' && monitoringSubTab === 'system-logs' && systemLogsView === 'system-event' && orgId) {
      loadOrgMembersForLogs(orgId);
      loadSystemEventLogs(orgId);
    }
  }, [tab, monitoringSubTab, systemLogsView, orgId, systemEventLogsFilters, systemEventLogsSearch, systemEventLogsSort, systemEventLogsPage, systemEventLogsPageSize, loadSystemEventLogs, loadOrgMembersForLogs]);

  React.useEffect(() => {
    if (tab === 'monitoring' && monitoringSubTab === 'system-logs' && systemLogsView === 'export' && orgId) {
      loadOrgMembersForLogs(orgId);
      loadExportLogs(orgId);
    }
  }, [tab, monitoringSubTab, systemLogsView, orgId, exportLogsFilters, exportLogsSearch, exportLogsSort, exportLogsPage, exportLogsPageSize, loadExportLogs, loadOrgMembersForLogs]);

  // Check if user is admin or owner
  React.useEffect(() => {
    (async () => {
      if (!orgId) return;
      try {
        const t = authService.getToken();
        const r = await fetch(`/api/orgs/${orgId}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
        if (r.ok) {
          const org = await r.json();
          const user = await authService.getUserInfo();
          const isAdmin = user?.roles?.some(r => r.toLowerCase() === 'admin') || false;
          const isOwner = org.ownerUserId === user?.id;
          setIsAdminOrOwner(isAdmin || isOwner);
        }
      } catch {}
    })();
  }, [orgId]);

  // Load usage dashboard data
  const loadDashboardData = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingDashboard(true);
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/usage/dashboard`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setDashboardData(data);
      }
    } catch {}
    finally { setLoadingDashboard(false); }
  }, []);

  const loadTrendsData = React.useCallback(async (id, period) => {
    if (!id) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/usage/trends?period=${period}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setTrendsData(Array.isArray(data) ? data : []);
      }
    } catch {}
  }, []);

  const loadTopConsumers = React.useCallback(async (id, type) => {
    if (!id || !isAdminOrOwner) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/usage/top-consumers?type=${type}&limit=10`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setTopConsumers(Array.isArray(data) ? data : []);
      }
    } catch {}
  }, [isAdminOrOwner]);

  const loadUsageByTeam = React.useCallback(async (id) => {
    if (!id || !isAdminOrOwner) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/usage/by-team`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setUsageByTeam(Array.isArray(data) ? data : []);
      }
    } catch {}
  }, [isAdminOrOwner]);

  const loadForecastData = React.useCallback(async (id) => {
    if (!id) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/usage/forecast`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setForecastData(data);
      }
    } catch {}
  }, []);

  const loadBalancesData = React.useCallback(async (id) => {
    if (!id) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${id}/billing/balances`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setBalancesData(data);
      }
    } catch {}
  }, []);

  const loadCreditBalance = React.useCallback(async (id) => {
    if (!id) return;
    setLoadingCreditBalance(true);
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/credits/balance/organization/${id}`, { headers: t ? { Authorization:`Bearer ${t}` } : {} });
      if (r.ok) {
        const data = await r.json();
        setCreditBalance(data);
      } else {
        const errorData = await r.json().catch(() => ({ message: `HTTP ${r.status}: ${r.statusText}` }));
        console.error('Failed to load credit balance:', errorData);
        setCreditBalance(null);
      }
    } catch (err) {
      console.error('Failed to load credit balance:', err);
      setCreditBalance(null);
    } finally {
      setLoadingCreditBalance(false);
    }
  }, []);

  const loadTeamsAndUsersForAllocation = React.useCallback(async () => {
    if (!orgId) return;
    try {
      // Load teams from credit balance (already loaded)
      if (creditBalance && creditBalance.teams) {
        setAvailableTeams(creditBalance.teams.map(team => ({
          id: team.team_id,
          name: team.team_name
        })));
      }
      
      // Collect all users from teams
      const allUsers = [];
      if (creditBalance && creditBalance.teams) {
        creditBalance.teams.forEach(team => {
          if (team.users && Array.isArray(team.users)) {
            team.users.forEach(user => {
              if (!allUsers.find(u => (u.user_id || u.userId) === (user.user_id || user.userId))) {
                allUsers.push(user);
              }
            });
          }
        });
      }
      setAvailableUsers(allUsers);
    } catch (err) {
      console.error('Failed to load teams/users for allocation:', err);
    }
  }, [orgId, creditBalance]);

  React.useEffect(() => {
    if (tab === 'billing' && billingSubTab === 'tokens' && orgId) {
      loadCreditBalance(orgId);
    }
  }, [tab, billingSubTab, orgId, loadCreditBalance]);

  React.useEffect(() => {
    if (tab === 'billing' && billingSubTab === 'dashboard' && orgId) {
      loadDashboardData(orgId);
      loadTrendsData(orgId, trendsPeriod);
      loadForecastData(orgId);
      loadBalancesData(orgId);
      if (isAdminOrOwner) {
        loadTopConsumers(orgId, topConsumersType);
        loadUsageByTeam(orgId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, billingSubTab, orgId, trendsPeriod, isAdminOrOwner, loadDashboardData, loadTrendsData, loadForecastData, loadBalancesData, loadTopConsumers, loadUsageByTeam]);

  React.useEffect(() => {
    if (tab === 'billing' && billingSubTab === 'dashboard' && orgId && isAdminOrOwner) {
      loadTopConsumers(orgId, topConsumersType);
    }
  }, [topConsumersType, orgId, isAdminOrOwner, tab, billingSubTab, loadTopConsumers]);

  const toggleScriptSharing = async () => {
    if (!orgId) return;
    try {
      setSavingScriptSharing(true);
      const t = authService.getToken();
      const newValue = !allowScriptSharing;
      const res = await fetch(`/api/orgs/${orgId}/script-sharing`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newValue)
      });
      if (res.ok) {
        setAllowScriptSharing(newValue);
      }
    } catch {}
    finally {
      setSavingScriptSharing(false);
    }
  };

  const saveModSettings = async () => {
    if (!orgId) return;
    try {
      setSavingMods(true);
      setModSaveMsg('');
      const t = authService.getToken();
      const body = { ...modSettings };
      const res = await fetch(`/api/orgs/${orgId}/moderation/settings`, { 
        method:'POST', 
        headers:{ 'Authorization': `Bearer ${t}`, 'Content-Type':'application/json' }, 
        body: JSON.stringify(body) 
      });
      if (res.ok) {
        setModSaveMsg('Moderation settings saved.');
      } else {
        setModSaveMsg('Failed to save moderation settings.');
      }
    } catch {
      setModSaveMsg('Failed to save moderation settings.');
    } finally { 
      setSavingMods(false); 
    }
  };

  const abbreviateId = (id) => {
    if (!id || id.length <= 8) return id;
    return `${id.substring(0, 4)}...${id.substring(id.length - 4)}`;
  };

  const copyId = async (userId) => {
    try {
      await navigator.clipboard.writeText(userId);
      setCopiedIds(prev => ({ ...prev, [userId]: true }));
      setTimeout(() => setCopiedIds(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      }), 1200);
    } catch {}
  };

  const upsertMember = async () => {
    if (!orgId || !addUserId.trim()) return;
    try {
      const t = authService.getToken();
      const body = { userId: addUserId.trim(), role: addRole };
      const r = await fetch(`/api/orgs/${orgId}/members`, { method:'POST', headers:{ Authorization:`Bearer ${t}`, 'Content-Type':'application/json' }, body: JSON.stringify(body) });
      if (r.ok) { setAddUserId(''); await loadMembers(orgId); }
    } catch {}
  };
  const removeMember = async (uid) => {
    if (!orgId) return;
    try {
      const t = authService.getToken();
      const r = await fetch(`/api/orgs/${orgId}/members/${encodeURIComponent(uid)}`, { method:'DELETE', headers:{ Authorization:`Bearer ${t}` } });
      if (r.ok) await loadMembers(orgId);
    } catch {}
  };
  const doInvite = async () => {
    setInviteMsg(''); setInviteLink('');
    if (!orgId || !inviteEmail.trim()) return;
    try {
      const t = authService.getToken();
      const body = { email: inviteEmail.trim(), firstName: inviteFirst || null, lastName: inviteLast || null, role: inviteRole, sendEmail: !!sendEmail };
      const r = await fetch(`/api/orgs/${orgId}/invite`, { method:'POST', headers:{ Authorization:`Bearer ${t}`, 'Content-Type':'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(()=>({}));
      if (!r.ok) { setInviteMsg(j?.message || 'Invite failed'); return; }
      setInviteLink(j.resetLink || '');
      setInviteMsg(j.emailed ? 'Email sent.' : (sendEmail && j.emailError ? `Email not sent: ${j.emailError}` : ''));
    } catch (e) { setInviteMsg(e.message || 'Invite failed'); }
  };
  const importCsv = async (file) => {
    if (!orgId || !file) return;
    setCsvBusy(true); setCsvLog([]);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).map(l=>l.trim()).filter(l=>l.length>0);
      const results = [];
      const t = authService.getToken();
      for (const [idx, line] of lines.entries()) {
        const parts = line.split(',').map(p => p.trim().replace(/^"(.*)"$/, '$1'));
        const email = parts[0] || '';
        const firstName = parts[1] || null;
        const lastName = parts[2] || null;
        let role = (parts[3] || 'member').toLowerCase();
        if (!['member','admin','owner'].includes(role)) role = 'member';
        const send = (parts[4] ?? '1').toString().trim();
        const sendEmailOpt = send === '0' ? false : true;
        if (!email) { results.push(`Row ${idx+1}: skipped (missing email)`); continue; }
        try {
          const body = { email, firstName, lastName, role, sendEmail: sendEmailOpt };
          const r = await fetch(`/api/orgs/${orgId}/invite`, { method:'POST', headers:{ Authorization:`Bearer ${t}`, 'Content-Type':'application/json' }, body: JSON.stringify(body) });
          const j = await r.json().catch(()=>({}));
          if (!r.ok) { results.push(`Row ${idx+1}: ERROR ${j?.message || r.status}`); }
          else { results.push(`Row ${idx+1}: OK ${email}`); }
        } catch (e) {
          results.push(`Row ${idx+1}: EXCEPTION ${(e && e.message) || 'unknown'}`);
        }
      }
      setCsvLog(results);
    } catch (e) {
      setCsvLog([`Failed to read file: ${(e && e.message) || 'unknown'}`]);
    } finally {
      setCsvBusy(false);
    }
  };

  if (!orgs.length) {
    return <div className="auth-container"><div className="auth-card"><div className="error-message">No organizations where you are an admin/owner.</div></div></div>;
  }

  const selectedOrg = orgs.find(o => o.id === orgId);
  const headerText = selectedOrg ? selectedOrg.name : 'Enterprise';

  return (
    <div style={{width:'100%', padding:'0 12px'}}>
      <h2 style={{ marginTop:0, fontSize: '28px', fontWeight: 600 }}>{headerText}</h2>
      <div style={{ display:'flex', gap:12, alignItems:'center', margin:'8px 0 12px 0' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Organization</span>
          <select 
            value={orgId ?? ''} 
            onChange={e=>setOrgId(Number(e.target.value)||null)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              cursor: 'pointer',
              minWidth: '200px'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          >
            {orgs.map(o => (<option key={o.id} value={o.id}>{o.name} ({o.slug})</option>))}
          </select>
        </label>
      </div>
      <div style={{ display:'flex', gap:0, marginBottom:12, borderBottom:'1px solid #e5e7eb' }}>
        <button 
          className="tab-button"
          style={getTabStyles(1, tab==='overview')}
          onMouseEnter={(e) => {
            if (tab !== 'overview') {
              Object.assign(e.currentTarget.style, getTabHoverStyles(1));
            }
          }}
          onMouseLeave={(e) => {
            if (tab !== 'overview') {
              restoreTabStyles(e.currentTarget, 1, false);
            }
          }}
          onClick={()=>setTab('overview')}
        >
          Overview
        </button>
        <button 
          className="tab-button"
          style={getTabStyles(1, tab==='users-and-access')}
          onMouseEnter={(e) => {
            if (tab !== 'users-and-access') {
              Object.assign(e.target.style, getTabHoverStyles(1));
            }
          }}
          onMouseLeave={(e) => {
            if (tab !== 'users-and-access') {
              restoreTabStyles(e.target, 1, false);
            }
          }}
          onClick={()=>{
            setTab('users-and-access');
            setUsersAccessSubTab('user-directory');
          }}
        >
          Users & Access Management
        </button>
        <button 
          className="tab-button"
          style={getTabStyles(1, tab==='moderation')}
          onMouseEnter={(e) => {
            if (tab !== 'moderation') {
              Object.assign(e.target.style, getTabHoverStyles(1));
            }
          }}
          onMouseLeave={(e) => {
            if (tab !== 'moderation') {
              restoreTabStyles(e.target, 1, false);
            }
          }}
          onClick={()=>setTab('moderation')}
        >
          Moderation
        </button>
        <button 
          className="tab-button"
          style={getTabStyles(1, tab==='billing')}
          onMouseEnter={(e) => {
            if (tab !== 'billing') {
              Object.assign(e.target.style, getTabHoverStyles(1));
            }
          }}
          onMouseLeave={(e) => {
            if (tab !== 'billing') {
              restoreTabStyles(e.target, 1, false);
            }
          }}
          onClick={()=>setTab('billing')}
        >
          Usage & Billing Management
        </button>
        <button 
          className="tab-button"
          style={getTabStyles(1, tab==='monitoring')}
          onMouseEnter={(e) => {
            if (tab !== 'monitoring') {
              Object.assign(e.target.style, getTabHoverStyles(1));
            }
          }}
          onMouseLeave={(e) => {
            if (tab !== 'monitoring') {
              restoreTabStyles(e.target, 1, false);
            }
          }}
          onClick={()=>setTab('monitoring')}
        >
          Monitoring & Analytics
        </button>
        <button 
          className="tab-button"
          style={getTabStyles(1, tab==='script-governance')}
          onMouseEnter={(e) => {
            if (tab !== 'script-governance') {
              Object.assign(e.target.style, getTabHoverStyles(1));
            }
          }}
          onMouseLeave={(e) => {
            if (tab !== 'script-governance') {
              restoreTabStyles(e.target, 1, false);
            }
          }}
          onClick={()=>setTab('script-governance')}
        >
          Script Management & Governance
        </button>
      </div>

      {tab==='overview' && (
        <div style={{ display:'grid', gap:12 }}>
          <div className="dashboard-card" style={{ padding:16 }}>
            <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Current Totals</h3>
            {loadingStats ? (
              <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>Loading statistics...</div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12 }}>
                <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                  <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>{stats.allUsers}</div>
                  <div style={{ color:'#6b7280', fontSize:14 }}>All Users</div>
                </div>
                <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                  <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>{stats.members}</div>
                  <div style={{ color:'#6b7280', fontSize:14 }}>Members</div>
                </div>
                <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                  <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>{stats.admins}</div>
                  <div style={{ color:'#6b7280', fontSize:14 }}>Admins</div>
                </div>
                <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                  <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>{stats.owners}</div>
                  <div style={{ color:'#6b7280', fontSize:14 }}>Owners</div>
                </div>
              </div>
            )}
          </div>
          <div className="dashboard-card" style={{ padding:16 }}>
            <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Script Sharing</h3>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', userSelect:'none' }}>
                <input
                  type="checkbox"
                  checked={allowScriptSharing}
                  onChange={toggleScriptSharing}
                  disabled={savingScriptSharing}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: savingScriptSharing ? 'not-allowed' : 'pointer',
                    accentColor: '#667eea'
                  }}
                />
                <span style={{ fontSize:14, color:'#374151' }}>
                  Allow script sharing within this organization
                </span>
              </label>
              {savingScriptSharing && (
                <span style={{ fontSize:12, color:'#6b7280' }}>Saving...</span>
              )}
            </div>
            <div style={{ marginTop:8, fontSize:12, color:'#6b7280' }}>
              When enabled, organization members can share BAIQL scripts with other members of this organization.
            </div>
          </div>
          <div className="dashboard-card" style={{ padding:8 }}>
            <div>Manage your organization's members and policies.</div>
          </div>
        </div>
      )}

      {tab==='users-and-access' && (
        <div>
          {/* Sub-tabs for Users & Access Management */}
          <div style={{ display:'flex', gap:0, marginBottom:12, borderBottom:'1px solid #e5e7eb' }}>
            <button 
              className="tab-button"
              style={getTabStyles(2, usersAccessSubTab==='user-directory')}
              onMouseEnter={(e) => {
                if (usersAccessSubTab !== 'user-directory') {
                  Object.assign(e.currentTarget.style, getTabHoverStyles(2));
                }
              }}
              onMouseLeave={(e) => {
                if (usersAccessSubTab !== 'user-directory') {
                  restoreTabStyles(e.currentTarget, 2, false);
                }
              }}
              onClick={()=>setUsersAccessSubTab('user-directory')}
            >
              User Directory
            </button>
            <button 
              className="tab-button"
              style={getTabStyles(2, usersAccessSubTab==='roles-permissions')}
              onMouseEnter={(e) => {
                if (usersAccessSubTab !== 'roles-permissions') {
                  Object.assign(e.target.style, getTabHoverStyles(2));
                }
              }}
              onMouseLeave={(e) => {
                if (usersAccessSubTab !== 'roles-permissions') {
                  restoreTabStyles(e.target, 2, false);
                }
              }}
              onClick={()=>setUsersAccessSubTab('roles-permissions')}
            >
              Roles & Permissions
            </button>
            <button 
              className="tab-button"
              style={getTabStyles(2, usersAccessSubTab==='authentication')}
              onMouseEnter={(e) => {
                if (usersAccessSubTab !== 'authentication') {
                  Object.assign(e.target.style, getTabHoverStyles(2));
                }
              }}
              onMouseLeave={(e) => {
                if (usersAccessSubTab !== 'authentication') {
                  restoreTabStyles(e.target, 2, false);
                }
              }}
              onClick={()=>setUsersAccessSubTab('authentication')}
            >
              Authentication
            </button>
            <button 
              className="tab-button"
              style={getTabStyles(2, usersAccessSubTab==='teams-groups')}
              onMouseEnter={(e) => {
                if (usersAccessSubTab !== 'teams-groups') {
                  Object.assign(e.target.style, getTabHoverStyles(2));
                }
              }}
              onMouseLeave={(e) => {
                if (usersAccessSubTab !== 'teams-groups') {
                  restoreTabStyles(e.target, 2, false);
                }
              }}
              onClick={()=>setUsersAccessSubTab('teams-groups')}
            >
              Teams & Groups
            </button>
            <button 
              className="tab-button"
              style={getTabStyles(2, usersAccessSubTab==='access-logs')}
              onMouseEnter={(e) => {
                if (usersAccessSubTab !== 'access-logs') {
                  Object.assign(e.target.style, getTabHoverStyles(2));
                }
              }}
              onMouseLeave={(e) => {
                if (usersAccessSubTab !== 'access-logs') {
                  restoreTabStyles(e.target, 2, false);
                }
              }}
              onClick={()=>setUsersAccessSubTab('access-logs')}
            >
              Access Logs
            </button>
          </div>

          {/* Sub-tab content */}
          {usersAccessSubTab==='user-directory' && (
        <div className="dashboard-card" style={{ padding:8, display:'grid', gap:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px', gap:8, alignItems:'end' }}>
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>User Id</span>
              <input 
                value={addUserId} 
                onChange={e=>setAddUserId(e.target.value)} 
                placeholder="paste user id"
                style={{
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Role</span>
              <select 
                value={addRole} 
                onChange={e=>setAddRole(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100%',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
                <option value="owner">owner</option>
              </select>
            </label>
            <button 
              onClick={upsertMember} 
              style={{ 
                height:'fit-content', 
                marginTop:'auto',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
              }}
              onMouseEnter={(e)=>{
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e)=>{
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
              }}
            >
              Add/Update
            </button>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 160px 120px', gap:12, alignItems:'start' }}>
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Email</span>
              <input 
                type="email" 
                value={inviteEmail} 
                onChange={e=>setInviteEmail(e.target.value)} 
                placeholder="user@example.com"
                style={{
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>First</span>
              <input 
                value={inviteFirst} 
                onChange={e=>setInviteFirst(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Last</span>
              <input 
                value={inviteLast} 
                onChange={e=>setInviteLast(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </label>
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Role</span>
              <select 
                value={inviteRole} 
                onChange={e=>setInviteRole(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  background: '#fff',
                  width: '100%',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
                onFocus={(e)=>{
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e)=>{
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
                <option value="owner">owner</option>
              </select>
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:6, marginTop:22 }}>
              <input type="checkbox" checked={sendEmail} onChange={e=>setSendEmail(e.target.checked)} />
              <span>Send email</span>
            </label>
            <div style={{ gridColumn:'1 / -1', display:'flex', gap:8, alignItems:'center' }}>
              <button 
                onClick={doInvite}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(102, 126, 234, 0.2)'
                }}
                onMouseEnter={(e)=>{
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
                onMouseLeave={(e)=>{
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                }}
              >
                Invite
              </button>
              {inviteMsg && <span style={{ color: inviteMsg.startsWith('Email sent') ? '#065f46' : '#b45309' }}>{inviteMsg}</span>}
            </div>
            {inviteLink && (
              <div style={{ gridColumn:'1 / -1', display:'grid', gridTemplateColumns:'1fr auto', gap:8 }}>
                <input 
                  value={inviteLink} 
                  readOnly
                  style={{
                    padding: '10px 12px',
                    border: '1.5px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#f9fafb',
                    width: '100%',
                    boxSizing: 'border-box',
                    color: '#6b7280'
                  }}
                />
                <button 
                  onClick={async()=>{ try{ await navigator.clipboard.writeText(inviteLink);}catch{} }}
                  style={{
                    padding: '10px 16px',
                    background: '#fff',
                    color: '#667eea',
                    border: '1.5px solid #667eea',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e)=>{
                    e.target.style.background = '#667eea';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e)=>{
                    e.target.style.background = '#fff';
                    e.target.style.color = '#667eea';
                  }}
                >
                  Copy
                </button>
              </div>
            )}
          </div>

          <div>
            <h3 style={{ margin:'12px 0 6px 0' }}>Members</h3>
            {busyMembers ? <div>Loading…</div> : (
              <div className="table-scroll">
                <div style={{ display:'flex', justifyContent:'flex-end', margin:'6px 0' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input type="checkbox" checked={showAllUsers} onChange={e=>setShowAllUsers(e.target.checked)} />
                    <span>Show All Users</span>
                  </label>
                </div>
                <table className="admin-table">
                  <thead><tr><th>Name</th><th>User Id</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {(showAllUsers ? members : members.filter(m => (m.status || (m.isActive === false ? 'deactivated' : 'active')) !== 'deactivated')).map(m => (
                      <tr key={m.userId}>
                        <td>{m.name || ''}</td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontFamily:'ui-monospace, Menlo, Monaco, Consolas, "Courier New", monospace' }}>
                              {abbreviateId(m.userId)}
                            </span>
                            <button
                              type="button"
                              aria-label="Copy user id"
                              title={copiedIds[m.userId] ? 'Copied!' : 'Copy'}
                              onClick={()=>copyId(m.userId)}
                              style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:24, height:24, border:'1px solid #d1d5d8', borderRadius:6, background:'transparent', cursor:'pointer', padding:0 }}
                            >
                              <span role="img" aria-hidden="true">📋</span>
                            </button>
                            {copiedIds[m.userId] && <span style={{ fontSize:12, color:'#059669' }}>Copied</span>}
                          </div>
                        </td>
                        <td>{m.functionalRole || m.displayRole || m.role}</td>
                        <td>{m.status || (m.isActive === false ? 'deactivated' : 'active')}</td>
                        <td>
                          <div style={{ display:'flex', gap:6 }}>
                            <button 
                              onClick={()=>removeMember(m.userId)}
                              style={{
                                padding: '6px 12px',
                                background: '#fff',
                                color: '#dc2626',
                                border: '1.5px solid #dc2626',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e)=>{
                                e.target.style.background = '#dc2626';
                                e.target.style.color = '#fff';
                              }}
                              onMouseLeave={(e)=>{
                                e.target.style.background = '#fff';
                                e.target.style.color = '#dc2626';
                              }}
                            >
                              Remove
                            </button>
                            <button 
                              onClick={async()=>{
                                if (!orgId) return;
                                try {
                                  const t = authService.getToken();
                                  const r = await fetch(`/api/orgs/${orgId}/members/${encodeURIComponent(m.userId)}/deactivate`, { method:'POST', headers:{ Authorization:`Bearer ${t}` } });
                                  if (r.ok) await loadMembers(orgId);
                                } catch {}
                              }}
                              style={{
                                padding: '6px 12px',
                                background: '#fff',
                                color: '#f59e0b',
                                border: '1.5px solid #f59e0b',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e)=>{
                                e.target.style.background = '#f59e0b';
                                e.target.style.color = '#fff';
                              }}
                              onMouseLeave={(e)=>{
                                e.target.style.background = '#fff';
                                e.target.style.color = '#f59e0b';
                              }}
                            >
                              Deactivate
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {members.length===0 && (<tr><td colSpan="5" style={{textAlign:'center'}}>No members</td></tr>)}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h3 style={{ margin:'12px 0 6px 0' }}>Bulk import</h3>
            <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
              <input 
                ref={fileInputRef}
                type="file" 
                accept=".csv" 
                onChange={e=>{ const f=e.target.files?.[0]; if (f) importCsv(f); e.target.value=''; }} 
                style={{ display:'none' }}
              />
              <button
                onClick={()=>fileInputRef.current?.click()}
                disabled={csvBusy}
                style={{
                  padding: '10px 20px',
                  background: csvBusy ? '#9ca3af' : '#fff',
                  color: csvBusy ? '#fff' : '#667eea',
                  border: `1.5px solid ${csvBusy ? '#9ca3af' : '#667eea'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: csvBusy ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: csvBusy ? 0.7 : 1
                }}
                onMouseEnter={(e)=>{
                  if (!csvBusy) {
                    e.target.style.background = '#667eea';
                    e.target.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e)=>{
                  if (!csvBusy) {
                    e.target.style.background = '#fff';
                    e.target.style.color = '#667eea';
                  }
                }}
              >
                {csvBusy ? 'Uploading…' : 'Choose File'}
              </button>
              <span style={{ color:'#6b7280' }}>CSV: email,first,last,role,sendEmail(1|0)</span>
            </div>
            {csvLog.length>0 && (
              <div style={{ marginTop:8 }}>
                <pre style={{ whiteSpace:'pre-wrap', background:'#f9fafb', padding:8, border:'1px solid #e5e7eb', borderRadius:6 }}>{csvLog.join('\n')}</pre>
              </div>
            )}
          </div>
        </div>
          )}

          {usersAccessSubTab==='roles-permissions' && orgId && (
            <RolesPermissionsTab 
              scopeType="Organization" 
              scopeId={orgId} 
              currentUserId={authService.getCurrentUser()?.id}
            />
          )}

          {usersAccessSubTab==='authentication' && orgId && (
            <AuthenticationTab 
              scopeType="Organization" 
              scopeId={orgId}
            />
          )}

          {usersAccessSubTab==='teams-groups' && (
            <div className="dashboard-card" style={{ padding: 24 }}>
              <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 20, fontWeight: 600 }}>
                Teams & Groups
              </h2>
              <p style={{ color: '#6b7280', fontSize: 14 }}>
                This feature is coming soon. You will be able to organize users into teams and groups for better access management.
              </p>
            </div>
          )}

          {usersAccessSubTab==='access-logs' && (
            <AccessLogs />
          )}

        </div>
      )}

      {tab==='script-governance' && orgId && (
        <ScriptGovernanceTab 
          scopeType="Organization" 
          scopeId={orgId} 
        />
      )}

      {tab==='moderation' && (
        <div>
          <h2 className="admin-header" style={{ marginTop:0, marginBottom:12 }}>Moderation</h2>
          
          <div className="dashboard-card" style={{ padding:16, marginBottom:12 }}>
            <h3 style={{ marginTop:0, marginBottom:12 }}>Toxicity / Hate / Violence / Self-harm Thresholds</h3>
            <div style={{ display:'grid', gap:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <label style={{ minWidth:180 }}>Hate warn threshold</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.05" 
                  value={modSettings.hateWarn}
                  onChange={(e)=>{
                    const val = Number(e.target.value);
                    setModSettings(prev => ({ ...prev, hateWarn: val }));
                  }}
                  style={{ flex:1 }}
                />
                <div style={{ minWidth: 50, textAlign:'right' }}><strong>{modSettings.hateWarn.toFixed(2)}</strong></div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <label style={{ minWidth:180 }}>Hate block threshold</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.05" 
                  value={modSettings.hateBlock}
                  onChange={(e)=>{
                    const val = Number(e.target.value);
                    setModSettings(prev => ({ ...prev, hateBlock: val }));
                  }}
                  style={{ flex:1 }}
                />
                <div style={{ minWidth: 50, textAlign:'right' }}><strong>{modSettings.hateBlock.toFixed(2)}</strong></div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <label style={{ minWidth:180 }}>Violence warn threshold</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.05" 
                  value={modSettings.violenceWarn}
                  onChange={(e)=>{
                    const val = Number(e.target.value);
                    setModSettings(prev => ({ ...prev, violenceWarn: val }));
                  }}
                  style={{ flex:1 }}
                />
                <div style={{ minWidth: 50, textAlign:'right' }}><strong>{modSettings.violenceWarn.toFixed(2)}</strong></div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <label style={{ minWidth:180 }}>Violence block threshold</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.05" 
                  value={modSettings.violenceBlock}
                  onChange={(e)=>{
                    const val = Number(e.target.value);
                    setModSettings(prev => ({ ...prev, violenceBlock: val }));
                  }}
                  style={{ flex:1 }}
                />
                <div style={{ minWidth: 50, textAlign:'right' }}><strong>{modSettings.violenceBlock.toFixed(2)}</strong></div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <label style={{ minWidth:180 }}>Self-harm warn threshold</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.05" 
                  value={modSettings.selfHarmWarn}
                  onChange={(e)=>{
                    const val = Number(e.target.value);
                    setModSettings(prev => ({ ...prev, selfHarmWarn: val }));
                  }}
                  style={{ flex:1 }}
                />
                <div style={{ minWidth: 50, textAlign:'right' }}><strong>{modSettings.selfHarmWarn.toFixed(2)}</strong></div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <label style={{ minWidth:180 }}>Self-harm block threshold</label>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.05" 
                  value={modSettings.selfHarmBlock}
                  onChange={(e)=>{
                    const val = Number(e.target.value);
                    setModSettings(prev => ({ ...prev, selfHarmBlock: val }));
                  }}
                  style={{ flex:1 }}
                />
                <div style={{ minWidth: 50, textAlign:'right' }}><strong>{modSettings.selfHarmBlock.toFixed(2)}</strong></div>
              </div>
            </div>
          </div>

          <div className="dashboard-card" style={{ padding:16, marginBottom:12 }}>
            <h3 style={{ marginTop:0, marginBottom:12 }}>Additional Controls</h3>
            <div style={{ display:'grid', gap:8 }}>
              <label style={{display:'flex', alignItems:'center', gap:8, margin:0}}>
                <input 
                  type="checkbox" 
                  checked={modSettings.blockPci}
                  onChange={(e)=>setModSettings(prev => ({ ...prev, blockPci: e.target.checked }))}
                />
                <span>Block PCI (payment card)</span>
              </label>
              <label style={{display:'flex', alignItems:'center', gap:8, margin:0}}>
                <input 
                  type="checkbox" 
                  checked={modSettings.blockCsam}
                  onChange={(e)=>setModSettings(prev => ({ ...prev, blockCsam: e.target.checked }))}
                />
                <span>Hard-block CSAM indicators</span>
              </label>
              <label style={{display:'flex', alignItems:'center', gap:8, margin:0}}>
                <input 
                  type="checkbox" 
                  checked={modSettings.forceDoNotStoreOnPiiWarn}
                  onChange={(e)=>setModSettings(prev => ({ ...prev, forceDoNotStoreOnPiiWarn: e.target.checked }))}
                />
                <span>Force DoNotStore on PII warn</span>
              </label>
            </div>
          </div>

          <div style={{display:'flex', justifyContent:'flex-end', alignItems:'center', gap:12, marginTop:12}}>
            {modSaveMsg && <span style={{ color: modSaveMsg.includes('saved') ? '#059669' : '#dc2626' }}>{modSaveMsg}</span>}
            <button 
              onClick={saveModSettings}
              disabled={savingMods}
              style={{
                padding: '12px 24px',
                background: savingMods ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: savingMods ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: savingMods ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.2)',
                opacity: savingMods ? 0.7 : 1
              }}
              onMouseEnter={(e)=>{
                if (!savingMods) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }
              }}
              onMouseLeave={(e)=>{
                if (!savingMods) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.2)';
                }
              }}
            >
              {savingMods ? 'Saving...' : 'Save Moderation Settings'}
            </button>
          </div>
        </div>
      )}

      {tab==='billing' && (
        <div>
          <h2 className="admin-header" style={{ marginTop:0, marginBottom:12 }}>Usage and Billing Management</h2>
          
          {/* Sub-tabs for billing sections */}
          <div style={{ display:'flex', gap:0, marginBottom:12, borderBottom:'1px solid #e5e7eb' }}>
            <button 
              className="tab-button"
              style={{
                background: billingSubTab==='dashboard' ? '#fff' : 'transparent',
                color: billingSubTab==='dashboard' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: billingSubTab==='dashboard' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: billingSubTab==='dashboard' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setBillingSubTab('dashboard')}
            >
              Usage Dashboard
            </button>
            <button 
              className="tab-button"
              style={{
                background: billingSubTab==='tokens' ? '#fff' : 'transparent',
                color: billingSubTab==='tokens' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: billingSubTab==='tokens' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: billingSubTab==='tokens' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setBillingSubTab('tokens')}
            >
              Token/Credit Management
            </button>
            <button 
              className="tab-button"
              style={{
                background: billingSubTab==='analytics' ? '#fff' : 'transparent',
                color: billingSubTab==='analytics' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: billingSubTab==='analytics' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: billingSubTab==='analytics' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setBillingSubTab('analytics')}
            >
              Cost Analytics
            </button>
            <button 
              className="tab-button"
              style={{
                background: billingSubTab==='settings' ? '#fff' : 'transparent',
                color: billingSubTab==='settings' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: billingSubTab==='settings' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: billingSubTab==='settings' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setBillingSubTab('settings')}
            >
              Billing Settings
            </button>
            <button 
              className="tab-button"
              style={{
                background: billingSubTab==='invoices' ? '#fff' : 'transparent',
                color: billingSubTab==='invoices' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: billingSubTab==='invoices' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: billingSubTab==='invoices' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setBillingSubTab('invoices')}
            >
              Invoices & Receipts
            </button>
            <button 
              className="tab-button"
              style={{
                background: billingSubTab==='budget' ? '#fff' : 'transparent',
                color: billingSubTab==='budget' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: billingSubTab==='budget' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: billingSubTab==='budget' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setBillingSubTab('budget')}
            >
              Budget Controls
            </button>
          </div>

          {billingSubTab==='dashboard' && (
            <div style={{ display:'grid', gap:12 }}>
              {/* Current Month Usage */}
              <div className="dashboard-card" style={{ padding:16 }}>
                <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Current Month Usage</h3>
                {loadingDashboard ? (
                  <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>Loading...</div>
                ) : dashboardData ? (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12 }}>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>{dashboardData.totalRequests?.toLocaleString() || 0}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Total Requests</div>
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>${((dashboardData.totalCostCents || 0) / 100).toFixed(2)}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Total Cost</div>
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>{((dashboardData.totalInputTokens || 0) + (dashboardData.totalOutputTokens || 0)).toLocaleString()}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Total Tokens</div>
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:28, fontWeight:700, color:'#111827', marginBottom:4 }}>${(dashboardData.avgCostPerRequest || 0).toFixed(4)}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Avg Cost/Request</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No data available</div>
                )}
              </div>

              {/* Usage Trends */}
              <div className="dashboard-card" style={{ padding:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <h3 style={{ marginTop:0, marginBottom:0, fontSize:18, fontWeight:600 }}>Usage Trends</h3>
                  <div style={{ display:'flex', gap:8 }}>
                    <select
                      value={trendsPeriod}
                      onChange={(e) => setTrendsPeriod(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#fff',
                        color: '#111827',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="7">Last 7 days</option>
                      <option value="30">Last 30 days</option>
                      <option value="90">Last 90 days</option>
                      <option value="mtd">Month to date</option>
                    </select>
                    <select
                      value={trendsView}
                      onChange={(e) => setTrendsView(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#fff',
                        color: '#111827',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="requests">Requests</option>
                      <option value="cost">Cost</option>
                      <option value="tokens">Tokens</option>
                    </select>
                  </div>
                </div>
                {trendsData.length > 0 ? (
                  <MiniLineChart
                    points={trendsData.map(d => ({
                      label: d.label,
                      value: trendsView === 'requests' ? d.requests : (trendsView === 'cost' ? (d.costCents / 100) : (d.inputTokens + d.outputTokens))
                    }))}
                    height={200}
                  />
                ) : (
                  <div style={{ color:'#6b7280', padding:'40px 0', textAlign:'center' }}>No trend data available</div>
                )}
              </div>

              {/* Top Consumers - Admin/Owner only */}
              {isAdminOrOwner && (
                <>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                      <h3 style={{ marginTop:0, marginBottom:0, fontSize:18, fontWeight:600 }}>Top Consumers</h3>
                      <div style={{ display:'flex', gap:8 }}>
                        <button
                          onClick={() => setTopConsumersType('users')}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            background: topConsumersType === 'users' ? '#667eea' : '#fff',
                            color: topConsumersType === 'users' ? '#fff' : '#111827',
                            cursor: 'pointer'
                          }}
                        >
                          Users
                        </button>
                        <button
                          onClick={() => setTopConsumersType('models')}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            background: topConsumersType === 'models' ? '#667eea' : '#fff',
                            color: topConsumersType === 'models' ? '#fff' : '#111827',
                            cursor: 'pointer'
                          }}
                        >
                          Models
                        </button>
                      </div>
                    </div>
                    {topConsumers.length > 0 ? (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom:'1px solid #e5e7eb' }}>
                              <th style={{ padding:'8px', textAlign:'left', fontSize:14, fontWeight:600, color:'#111827' }}>Name</th>
                              <th style={{ padding:'8px', textAlign:'right', fontSize:14, fontWeight:600, color:'#111827' }}>Requests</th>
                              <th style={{ padding:'8px', textAlign:'right', fontSize:14, fontWeight:600, color:'#111827' }}>Cost</th>
                              <th style={{ padding:'8px', textAlign:'right', fontSize:14, fontWeight:600, color:'#111827' }}>Tokens</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topConsumers.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom:'1px solid #f3f4f6' }}>
                                <td style={{ padding:'8px', fontSize:14, color:'#111827' }}>{item.name || item.userId?.substring(0, 8)}</td>
                                <td style={{ padding:'8px', textAlign:'right', fontSize:14, color:'#111827' }}>{item.requests?.toLocaleString() || 0}</td>
                                <td style={{ padding:'8px', textAlign:'right', fontSize:14, color:'#111827' }}>${((item.costCents || 0) / 100).toFixed(2)}</td>
                                <td style={{ padding:'8px', textAlign:'right', fontSize:14, color:'#111827' }}>{((item.inputTokens || 0) + (item.outputTokens || 0)).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No consumer data available</div>
                    )}
                  </div>

                  {/* Usage by Team */}
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Usage by Team/Department</h3>
                    {usageByTeam.length > 0 ? (
                      <HorizontalBarChart
                        items={usageByTeam.map(t => ({
                          label: t.name || `Team ${t.teamId}`,
                          value: t.costCents || 0
                        }))}
                        height={Math.max(200, usageByTeam.length * 28)}
                      />
                    ) : (
                      <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No usage data by team available</div>
                    )}
                  </div>
                </>
              )}

              {/* Forecast Projections */}
              <div className="dashboard-card" style={{ padding:16 }}>
                <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Forecast Projections (Next 30 Days)</h3>
                {forecastData ? (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12 }}>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:24, fontWeight:700, color:'#111827', marginBottom:4 }}>{forecastData.projectedRequests?.toLocaleString() || 0}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Projected Requests</div>
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:24, fontWeight:700, color:'#111827', marginBottom:4 }}>${((forecastData.projectedCostCents || 0) / 100).toFixed(2)}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Projected Cost</div>
                      {forecastData.trend && (
                        <div style={{ fontSize:12, color:forecastData.trend === 'increasing' ? '#dc2626' : (forecastData.trend === 'decreasing' ? '#059669' : '#6b7280'), marginTop:4 }}>
                          {forecastData.trend === 'increasing' ? '↑ Increasing' : (forecastData.trend === 'decreasing' ? '↓ Decreasing' : '→ Stable')}
                        </div>
                      )}
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:24, fontWeight:700, color:'#111827', marginBottom:4 }}>{((forecastData.projectedInputTokens || 0) + (forecastData.projectedOutputTokens || 0)).toLocaleString()}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Projected Tokens</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No forecast data available</div>
                )}
              </div>

              {/* Balances */}
              {balancesData && (
                <div className="dashboard-card" style={{ padding:16 }}>
                  <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Balances</h3>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:24, fontWeight:700, color:'#111827', marginBottom:4 }}>${((balancesData.orgTotal || 0) / 100).toFixed(2)}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Organization Total</div>
                    </div>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', textAlign:'center', background:'#fff' }}>
                      <div style={{ fontSize:24, fontWeight:700, color:'#111827', marginBottom:4 }}>${((balancesData.companyTotal || 0) / 100).toFixed(2)}</div>
                      <div style={{ color:'#6b7280', fontSize:14 }}>Company Total</div>
                    </div>
                  </div>
                  {balancesData.individual && balancesData.individual.length > 0 && (
                    <div style={{ marginTop:16 }}>
                      <h4 style={{ marginTop:0, marginBottom:12, fontSize:16, fontWeight:600 }}>Individual Balances</h4>
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom:'1px solid #e5e7eb' }}>
                              <th style={{ padding:'8px', textAlign:'left', fontSize:14, fontWeight:600, color:'#111827' }}>Name</th>
                              <th style={{ padding:'8px', textAlign:'right', fontSize:14, fontWeight:600, color:'#111827' }}>Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {balancesData.individual.map((item, idx) => (
                              <tr key={idx} style={{ borderBottom:'1px solid #f3f4f6' }}>
                                <td style={{ padding:'8px', fontSize:14, color:'#111827' }}>{item.name || item.email || item.userId?.substring(0, 8)}</td>
                                <td style={{ padding:'8px', textAlign:'right', fontSize:14, color:'#111827' }}>${((item.balanceCents || 0) / 100).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}


          {billingSubTab==='tokens' && (
            <div style={{ display:'grid', gap:12 }}>
              {/* Credit Management Sub-Navigation */}
              <div style={{ display:'flex', gap:8, borderBottom:'1px solid #e5e7eb', paddingBottom:8 }}>
                <button
                  className="tab-button"
                  style={{
                    background: creditManagementView==='balance' ? '#fff' : 'transparent',
                    color: creditManagementView==='balance' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: creditManagementView==='balance' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: creditManagementView==='balance' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setCreditManagementView('balance')}
                >
                  Current Balance
                </button>
                <button
                  className="tab-button"
                  style={{
                    background: creditManagementView==='history' ? '#fff' : 'transparent',
                    color: creditManagementView==='history' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: creditManagementView==='history' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: creditManagementView==='history' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setCreditManagementView('history')}
                >
                  Transaction History
                </button>
                <button
                  className="tab-button"
                  style={{
                    background: creditManagementView==='expiration' ? '#fff' : 'transparent',
                    color: creditManagementView==='expiration' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: creditManagementView==='expiration' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: creditManagementView==='expiration' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setCreditManagementView('expiration')}
                >
                  Expiration Management
                </button>
                <button
                  className="tab-button"
                  style={{
                    background: creditManagementView==='auto-recharge' ? '#fff' : 'transparent',
                    color: creditManagementView==='auto-recharge' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: creditManagementView==='auto-recharge' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: creditManagementView==='auto-recharge' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setCreditManagementView('auto-recharge')}
                >
                  Auto-Recharge Rules
                </button>
                <button
                  className="tab-button"
                  style={{
                    background: creditManagementView==='subscription' ? '#fff' : 'transparent',
                    color: creditManagementView==='subscription' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: creditManagementView==='subscription' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: creditManagementView==='subscription' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setCreditManagementView('subscription')}
                >
                  Subscription Management
                </button>
              </div>

              {/* Current Balance View */}
              {creditManagementView==='balance' && (
                <>
                  {loadingCreditBalance ? (
                    <div style={{ padding:40, textAlign:'center', color:'#6b7280' }}>Loading credit balance...</div>
                  ) : creditBalance ? (
                    <>
                      {/* Organization Balance Card */}
                      <div className="dashboard-card" style={{ padding:16 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                          <h3 style={{ marginTop:0, marginBottom:0, fontSize:18, fontWeight:600 }}>Credit Balance Overview</h3>
                          <button 
                            className="btn-primary"
                            onClick={() => setShowPurchaseModal(true)}
                            style={{ padding:'8px 16px', fontSize:14 }}
                          >
                            Purchase Credits
                          </button>
                        </div>
                    
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:16, marginBottom:16 }}>
                      <div style={{ padding:16, background:'#f9fafb', borderRadius:8 }}>
                        <div style={{ fontSize:14, color:'#6b7280', marginBottom:4 }}>Organization Balance</div>
                        <div style={{ fontSize:24, fontWeight:600, color:'#111827' }}>
                          ${creditBalance.organization?.total_balance?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize:12, color:'#6b7280', marginTop:4 }}>
                          {creditBalance.organization?.total_balance?.toFixed(2) || '0.00'} credits available
                        </div>
                      </div>
                      
                      <div style={{ padding:16, background:'#f9fafb', borderRadius:8 }}>
                        <div style={{ fontSize:14, color:'#6b7280', marginBottom:4 }}>Unallocated</div>
                        <div style={{ fontSize:20, fontWeight:600, color:'#111827' }}>
                          ${creditBalance.organization?.unallocated?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize:12, color:'#6b7280', marginTop:4 }}>
                          {creditBalance.organization?.unallocated && creditBalance.organization?.total_balance ? 
                            ((creditBalance.organization.unallocated / creditBalance.organization.total_balance) * 100).toFixed(1) : '0'}%
                        </div>
                      </div>
                      
                      <div style={{ padding:16, background:'#f9fafb', borderRadius:8 }}>
                        <div style={{ fontSize:14, color:'#6b7280', marginBottom:4 }}>Allocated</div>
                        <div style={{ fontSize:20, fontWeight:600, color:'#111827' }}>
                          ${creditBalance.organization?.allocated?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12, paddingTop:16, borderTop:'1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize:12, color:'#6b7280', marginBottom:4 }}>This Month Spent</div>
                        <div style={{ fontSize:16, fontWeight:600 }}>${creditBalance.stats?.month_to_date_spent?.toFixed(2) || '0.00'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:12, color:'#6b7280', marginBottom:4 }}>Daily Average (30d)</div>
                        <div style={{ fontSize:16, fontWeight:600 }}>${creditBalance.stats?.daily_average_30d?.toFixed(2) || '0.00'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:12, color:'#6b7280', marginBottom:4 }}>Projected Runout</div>
                        <div style={{ fontSize:16, fontWeight:600 }}>
                          {creditBalance.stats?.projected_runout_days || 0} days
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:12, color:'#6b7280', marginBottom:4 }}>Expiring Soon</div>
                        <div style={{ fontSize:16, fontWeight:600, color:creditBalance.stats?.expiring_soon > 0 ? '#dc2626' : '#111827' }}>
                          ${creditBalance.stats?.expiring_soon?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Credit Distribution - Teams */}
                  {creditBalance.teams && creditBalance.teams.length > 0 && (
                    <div className="dashboard-card" style={{ padding:16 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                        <h3 style={{ marginTop:0, marginBottom:0, fontSize:18, fontWeight:600 }}>Credit Distribution by Team</h3>
                        {creditBalance.organization && (
                          <button 
                            className="btn btn-secondary"
                            onClick={() => {
                              loadTeamsAndUsersForAllocation();
                              setShowAllocationModal(true);
                            }}
                            style={{ padding:'8px 16px', fontSize:14 }}
                          >
                            Allocate Credits
                          </button>
                        )}
                      </div>
                      
                      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        {creditBalance.teams.map((team, idx) => (
                          <div key={team.team_id || idx} style={{ border:'1px solid #e5e7eb', borderRadius:6, padding:12 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                              <div style={{ fontWeight:600, fontSize:16 }}>{team.team_name}</div>
                              <div style={{ display:'flex', gap:16 }}>
                                <div style={{ textAlign:'right' }}>
                                  <div style={{ fontSize:12, color:'#6b7280' }}>Balance</div>
                                  <div style={{ fontSize:16, fontWeight:600 }}>${team.balance?.toFixed(2) || '0.00'}</div>
                                </div>
                                <div style={{ textAlign:'right' }}>
                                  <div style={{ fontSize:12, color:'#6b7280' }}>Allocated</div>
                                  <div style={{ fontSize:14 }}>${team.allocated?.toFixed(2) || '0.00'}</div>
                                </div>
                                <div style={{ textAlign:'right' }}>
                                  <div style={{ fontSize:12, color:'#6b7280' }}>Consumed</div>
                                  <div style={{ fontSize:14 }}>${team.consumed?.toFixed(2) || '0.00'}</div>
                                </div>
                              </div>
                            </div>
                            
                            {team.users && team.users.length > 0 && (
                              <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #e5e7eb' }}>
                                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:12, fontSize:14 }}>
                                  {team.users.map((user, uIdx) => (
                                    <React.Fragment key={user.user_id || uIdx}>
                                      <div style={{ paddingLeft:16 }}>{user.email}</div>
                                      <div style={{ textAlign:'right', fontWeight:600 }}>${user.balance?.toFixed(2) || '0.00'}</div>
                                      <div style={{ textAlign:'right', color:'#6b7280' }}>${user.allocated?.toFixed(2) || '0.00'}</div>
                                      <div style={{ textAlign:'right', color:'#6b7280' }}>${user.consumed?.toFixed(2) || '0.00'}</div>
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding:40, textAlign:'center', color:'#6b7280' }}>Failed to load credit balance</div>
              )}
            </>
          )}

          {/* Transaction History View */}
          {creditManagementView==='history' && orgId && (
            <CreditHistory 
              organizationId={orgId}
            />
          )}

          {/* Expiration Management View */}
          {creditManagementView==='expiration' && orgId && (
            <CreditExpiration 
              organizationId={orgId}
            />
          )}

          {/* Auto-Recharge Rules View */}
          {creditManagementView==='auto-recharge' && orgId && (
            <AutoRechargeRules 
              organizationId={orgId}
              accountId={creditBalance?.organization?.account_id}
            />
          )}

          {/* Subscription Management View */}
          {creditManagementView==='subscription' && orgId && (
            <SubscriptionManagement 
              organizationId={orgId}
            />
          )}
        </div>
      )}

          {billingSubTab==='analytics' && (
            <CostAnalytics orgId={orgId} />
          )}

          {billingSubTab==='settings' && (
            <BillingSettings orgId={orgId} />
          )}

          {billingSubTab==='invoices' && (
            <InvoicesReceipts orgId={orgId} />
          )}

          {billingSubTab==='budget' && (
            <BudgetControls orgId={orgId} />
          )}
        </div>
      )}


      {tab==='monitoring' && (
        <div>
          <h2 className="admin-header" style={{ marginTop:0, marginBottom:12 }}>Monitoring & Analytics</h2>
          
          {/* Sub-tabs for monitoring sections */}
          <div style={{ display:'flex', gap:0, marginBottom:12, borderBottom:'1px solid #e5e7eb' }}>
            <button 
              className="tab-button"
              style={{
                background: monitoringSubTab==='system-logs' ? '#fff' : 'transparent',
                color: monitoringSubTab==='system-logs' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: monitoringSubTab==='system-logs' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: monitoringSubTab==='system-logs' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setMonitoringSubTab('system-logs')}
            >
              System Logs
            </button>
            <button 
              className="tab-button"
              style={{
                background: monitoringSubTab==='real-time' ? '#fff' : 'transparent',
                color: monitoringSubTab==='real-time' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: monitoringSubTab==='real-time' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: monitoringSubTab==='real-time' ? 600 : 400,
                fontSize: '14px'
              }}
              onClick={()=>setMonitoringSubTab('real-time')}
            >
              Real-Time Dashboard
            </button>
            <button 
              className="tab-button"
              style={{
                background: monitoringSubTab==='performance' ? '#fff' : 'transparent',
                color: monitoringSubTab==='performance' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: monitoringSubTab==='performance' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'not-allowed',
                fontWeight: monitoringSubTab==='performance' ? 600 : 400,
                fontSize: '14px'
              }}
              disabled
            >
              Performance Monitoring
            </button>
            <button 
              className="tab-button"
              style={{
                background: monitoringSubTab==='alerting' ? '#fff' : 'transparent',
                color: monitoringSubTab==='alerting' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: monitoringSubTab==='alerting' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: monitoringSubTab==='alerting' ? 600 : 400,
                fontSize: '14px'
              }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMonitoringSubTab('alerting');
              }}
            >
              Alerting System
            </button>
            <button 
              className="tab-button"
              style={{
                background: monitoringSubTab==='user-analytics' ? '#fff' : 'transparent',
                color: monitoringSubTab==='user-analytics' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: monitoringSubTab==='user-analytics' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'not-allowed',
                fontWeight: monitoringSubTab==='user-analytics' ? 600 : 400,
                fontSize: '14px'
              }}
              disabled
            >
              User Analytics
            </button>
            <button 
              className="tab-button"
              style={{
                background: monitoringSubTab==='script-analytics' ? '#fff' : 'transparent',
                color: monitoringSubTab==='script-analytics' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: monitoringSubTab==='script-analytics' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'not-allowed',
                fontWeight: monitoringSubTab==='script-analytics' ? 600 : 400,
                fontSize: '14px'
              }}
              disabled
            >
              Script Analytics
            </button>
            <button 
              className="tab-button"
              style={{
                background: monitoringSubTab==='custom-reports' ? '#fff' : 'transparent',
                color: monitoringSubTab==='custom-reports' ? '#111827' : '#6b7280',
                border: 'none',
                borderBottom: monitoringSubTab==='custom-reports' ? '2px solid #111827' : '2px solid transparent',
                padding: '8px 16px',
                cursor: 'not-allowed',
                fontWeight: monitoringSubTab==='custom-reports' ? 600 : 400,
                fontSize: '14px'
              }}
              disabled
            >
              Custom Reports
            </button>
          </div>

          {monitoringSubTab==='system-logs' && (
            <div>
              {/* System Logs view selector */}
              <div style={{ display:'flex', gap:0, marginBottom:12, borderBottom:'1px solid #e5e7eb' }}>
                <button 
                  className="tab-button"
                  style={{
                    background: systemLogsView==='execution' ? '#fff' : 'transparent',
                    color: systemLogsView==='execution' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: systemLogsView==='execution' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: systemLogsView==='execution' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setSystemLogsView('execution')}
                >
                  Execution Logs
                </button>
                <button 
                  className="tab-button"
                  style={{
                    background: systemLogsView==='error' ? '#fff' : 'transparent',
                    color: systemLogsView==='error' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: systemLogsView==='error' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: systemLogsView==='error' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setSystemLogsView('error')}
                >
                  Error Logs
                </button>
                <button 
                  className="tab-button"
                  style={{
                    background: systemLogsView==='api' ? '#fff' : 'transparent',
                    color: systemLogsView==='api' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: systemLogsView==='api' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: systemLogsView==='api' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setSystemLogsView('api')}
                >
                  API Logs
                </button>
                <button 
                  className="tab-button"
                  style={{
                    background: systemLogsView==='system-event' ? '#fff' : 'transparent',
                    color: systemLogsView==='system-event' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: systemLogsView==='system-event' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: systemLogsView==='system-event' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setSystemLogsView('system-event')}
                >
                  System Event Logs
                </button>
                <button 
                  className="tab-button"
                  style={{
                    background: systemLogsView==='export' ? '#fff' : 'transparent',
                    color: systemLogsView==='export' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderBottom: systemLogsView==='export' ? '2px solid #111827' : '2px solid transparent',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: systemLogsView==='export' ? 600 : 400,
                    fontSize: '14px'
                  }}
                  onClick={()=>setSystemLogsView('export')}
                >
                  Export Logs
                </button>
              </div>

              {systemLogsView==='execution' && (
                <div style={{ display:'grid', gap:12 }}>
                  {/* Filters */}
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Filters</h3>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Start Date</span>
                        <input 
                          type="date"
                          value={executionLogsFilters.startDate ? executionLogsFilters.startDate.toISOString().split('T')[0] : ''}
                          onChange={e=>setExecutionLogsFilters({...executionLogsFilters, startDate: e.target.value ? new Date(e.target.value) : null})}
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff'
                          }}
                        />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>End Date</span>
                        <input 
                          type="date"
                          value={executionLogsFilters.endDate ? executionLogsFilters.endDate.toISOString().split('T')[0] : ''}
                          onChange={e=>setExecutionLogsFilters({...executionLogsFilters, endDate: e.target.value ? new Date(e.target.value) : null})}
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff'
                          }}
                        />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>User</span>
                        <select 
                          value={executionLogsFilters.userId}
                          onChange={e=>setExecutionLogsFilters({...executionLogsFilters, userId: e.target.value})}
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">All Users</option>
                          {orgMembers.map(m => (
                            <option key={m.UserId} value={m.UserId}>{m.email || m.name || m.UserId.substring(0, 8)}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Script Name</span>
                        <input 
                          type="text"
                          value={executionLogsFilters.scriptName}
                          onChange={e=>setExecutionLogsFilters({...executionLogsFilters, scriptName: e.target.value})}
                          placeholder="Search script name"
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff'
                          }}
                        />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Status</span>
                        <select 
                          multiple
                          value={executionLogsFilters.status}
                          onChange={e=>{
                            const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                            setExecutionLogsFilters({...executionLogsFilters, status: selected});
                          }}
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            cursor: 'pointer',
                            minHeight: '80px'
                          }}
                        >
                          <option value="queued">Queued</option>
                          <option value="running">Running</option>
                          <option value="completed">Completed</option>
                          <option value="failed">Failed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="timeout">Timeout</option>
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Search</span>
                        <input 
                          type="text"
                          value={executionLogsSearch}
                          onChange={e=>setExecutionLogsSearch(e.target.value)}
                          placeholder="Search across all fields"
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff'
                          }}
                        />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Sort By</span>
                        <select 
                          value={executionLogsSort.field}
                          onChange={e=>setExecutionLogsSort({...executionLogsSort, field: e.target.value})}
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="startedAt">Started At</option>
                          <option value="duration">Duration</option>
                          <option value="cost">Cost</option>
                          <option value="rows">Rows Processed</option>
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Sort Direction</span>
                        <select 
                          value={executionLogsSort.direction}
                          onChange={e=>setExecutionLogsSort({...executionLogsSort, direction: e.target.value})}
                          style={{
                            padding: '10px 12px',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="desc">Descending</option>
                          <option value="asc">Ascending</option>
                        </select>
                      </label>
                    </div>
                    <button
                      onClick={()=>loadExecutionLogs(orgId)}
                      style={{
                        marginTop: 12,
                        padding: '10px 20px',
                        background: '#667eea',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e)=>e.target.style.background='#5568d3'}
                      onMouseOut={(e)=>e.target.style.background='#667eea'}
                    >
                      Apply Filters
                    </button>
                  </div>

                  {/* Execution Logs Table */}
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Execution Logs</h3>
                    {loadingExecutionLogs ? (
                      <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>Loading execution logs...</div>
                    ) : executionLogs.length === 0 ? (
                      <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No execution logs found</div>
                    ) : (
                      <>
                        <div style={{ overflowX:'auto' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'14px' }}>
                            <thead>
                              <tr style={{ borderBottom:'2px solid #e5e7eb', background:'#f9fafb' }}>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600, color:'#374151' }}>Started</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600, color:'#374151' }}>User</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600, color:'#374151' }}>Script</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600, color:'#374151' }}>Duration</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600, color:'#374151' }}>Status</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600, color:'#374151' }}>Rows</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600, color:'#374151' }}>Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {executionLogs.map(log => {
                                const statusColors = {
                                  completed: '#10b981',
                                  failed: '#ef4444',
                                  running: '#3b82f6',
                                  queued: '#6b7280',
                                  cancelled: '#f59e0b',
                                  timeout: '#f97316'
                                };
                                const statusColor = statusColors[log.status?.toLowerCase()] || '#6b7280';
                                return (
                                  <tr 
                                    key={log.executionId}
                                    onClick={()=>loadExecutionLogDetail(orgId, log.executionId)}
                                    style={{ 
                                      borderBottom:'1px solid #e5e7eb', 
                                      cursor:'pointer',
                                      transition: 'background 0.2s ease'
                                    }}
                                    onMouseOver={(e)=>e.currentTarget.style.background='#f9fafb'}
                                    onMouseOut={(e)=>e.currentTarget.style.background='transparent'}
                                  >
                                    <td style={{ padding:'12px', color:'#111827' }}>
                                      {log.startedAt ? new Date(log.startedAt).toLocaleString() : '-'}
                                    </td>
                                    <td style={{ padding:'12px', color:'#111827' }}>{log.userEmail || log.userId?.substring(0, 8)}</td>
                                    <td style={{ padding:'12px', color:'#111827' }}>{log.scriptName || 'Ad-hoc'}</td>
                                    <td style={{ padding:'12px', color:'#111827' }}>
                                      {log.durationSeconds ? `${Math.round(log.durationSeconds)}s` : '-'}
                                    </td>
                                    <td style={{ padding:'12px' }}>
                                      <span style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        background: `${statusColor}20`,
                                        color: statusColor
                                      }}>
                                        {log.status || '-'}
                                      </span>
                                    </td>
                                    <td style={{ padding:'12px', color:'#111827' }}>
                                      {log.rowsProcessed != null ? log.rowsProcessed.toLocaleString() : '-'}
                                    </td>
                                    <td style={{ padding:'12px', color:'#111827' }}>
                                      ${log.estimatedCost?.toFixed(4) || '0.0000'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {/* Pagination */}
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16, paddingTop:16, borderTop:'1px solid #e5e7eb' }}>
                          <div style={{ fontSize:14, color:'#6b7280' }}>
                            Showing {((executionLogsPage - 1) * executionLogsPageSize) + 1} to {Math.min(executionLogsPage * executionLogsPageSize, executionLogsTotal)} of {executionLogsTotal} results
                          </div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button
                              onClick={()=>setExecutionLogsPage(p => Math.max(1, p - 1))}
                              disabled={executionLogsPage === 1}
                              style={{
                                padding: '8px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                background: executionLogsPage === 1 ? '#f3f4f6' : '#fff',
                                color: executionLogsPage === 1 ? '#9ca3af' : '#111827',
                                cursor: executionLogsPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              Previous
                            </button>
                            <button
                              onClick={()=>setExecutionLogsPage(p => p + 1)}
                              disabled={executionLogsPage * executionLogsPageSize >= executionLogsTotal}
                              style={{
                                padding: '8px 16px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                background: executionLogsPage * executionLogsPageSize >= executionLogsTotal ? '#f3f4f6' : '#fff',
                                color: executionLogsPage * executionLogsPageSize >= executionLogsTotal ? '#9ca3af' : '#111827',
                                cursor: executionLogsPage * executionLogsPageSize >= executionLogsTotal ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Detail Modal */}
                  {selectedExecutionLog && (
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000
                    }} onClick={()=>setSelectedExecutionLog(null)}>
                      <div style={{
                        background: '#fff',
                        borderRadius: '8px',
                        padding: '24px',
                        maxWidth: '800px',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        width: '90%'
                      }} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                          <h3 style={{ margin:0, fontSize:20, fontWeight:600 }}>Execution Details</h3>
                          <button
                            onClick={()=>setSelectedExecutionLog(null)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              fontSize: '24px',
                              cursor: 'pointer',
                              color: '#6b7280'
                            }}
                          >
                            ×
                          </button>
                        </div>
                        <div style={{ display:'grid', gap:12 }}>
                          <div><strong>Execution ID:</strong> {selectedExecutionLog.executionId}</div>
                          <div><strong>Script:</strong> {selectedExecutionLog.scriptName} {selectedExecutionLog.scriptVersion ? `(${selectedExecutionLog.scriptVersion})` : ''}</div>
                          <div><strong>User:</strong> {selectedExecutionLog.userName || selectedExecutionLog.userEmail}</div>
                          <div><strong>Status:</strong> {selectedExecutionLog.status}</div>
                          <div><strong>Started:</strong> {selectedExecutionLog.startedAt ? new Date(selectedExecutionLog.startedAt).toLocaleString() : '-'}</div>
                          <div><strong>Completed:</strong> {selectedExecutionLog.completedAt ? new Date(selectedExecutionLog.completedAt).toLocaleString() : '-'}</div>
                          <div><strong>Duration:</strong> {selectedExecutionLog.durationSeconds ? `${Math.round(selectedExecutionLog.durationSeconds)}s` : '-'}</div>
                          {selectedExecutionLog.rowsProcessed != null && (
                            <div><strong>Rows Processed:</strong> {selectedExecutionLog.rowsProcessed.toLocaleString()}</div>
                          )}
                          {selectedExecutionLog.tokensConsumed > 0 && (
                            <div><strong>Tokens Consumed:</strong> {selectedExecutionLog.tokensConsumed.toLocaleString()}</div>
                          )}
                          <div><strong>Estimated Cost:</strong> ${selectedExecutionLog.estimatedCost?.toFixed(6) || '0.000000'}</div>
                          {selectedExecutionLog.errorMessage && (
                            <div style={{ color:'#ef4444' }}><strong>Error:</strong> {selectedExecutionLog.errorMessage}</div>
                          )}
                          {selectedExecutionLog.logs && selectedExecutionLog.logs.length > 0 && (
                            <div>
                              <strong>Logs:</strong>
                              <div style={{ marginTop:8, padding:12, background:'#f9fafb', borderRadius:'8px', maxHeight:'200px', overflow:'auto', fontSize:'12px', fontFamily:'monospace' }}>
                                {selectedExecutionLog.logs.map((log, idx) => (
                                  <div key={idx} style={{ marginBottom:4 }}>{log}</div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {systemLogsView==='error' && (
                <div style={{ display:'grid', gap:12 }}>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Filters</h3>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Start Date</span>
                        <input type="date" value={errorLogsFilters.startDate ? errorLogsFilters.startDate.toISOString().split('T')[0] : ''} onChange={e=>setErrorLogsFilters({...errorLogsFilters, startDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>End Date</span>
                        <input type="date" value={errorLogsFilters.endDate ? errorLogsFilters.endDate.toISOString().split('T')[0] : ''} onChange={e=>setErrorLogsFilters({...errorLogsFilters, endDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Severity</span>
                        <select multiple value={errorLogsFilters.severity} onChange={e=>{ const selected = Array.from(e.target.selectedOptions, opt => opt.value); setErrorLogsFilters({...errorLogsFilters, severity: selected}); }} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff', cursor: 'pointer', minHeight: '80px' }}>
                          <option value="CRITICAL">Critical</option>
                          <option value="ERROR">Error</option>
                          <option value="WARNING">Warning</option>
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>User</span>
                        <select value={errorLogsFilters.userId} onChange={e=>setErrorLogsFilters({...errorLogsFilters, userId: e.target.value})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff', cursor: 'pointer' }}>
                          <option value="">All Users</option>
                          {orgMembers.map(m => <option key={m.UserId} value={m.UserId}>{m.email || m.name || m.UserId.substring(0, 8)}</option>)}
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Search</span>
                        <input type="text" value={errorLogsSearch} onChange={e=>setErrorLogsSearch(e.target.value)} placeholder="Search error messages" style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                    </div>
                    <button onClick={()=>loadErrorLogs(orgId)} style={{ marginTop: 12, padding: '10px 20px', background: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>Apply Filters</button>
                  </div>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Error Logs</h3>
                    {loadingErrorLogs ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>Loading...</div> : errorLogs.length === 0 ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No error logs found</div> : (
                      <>
                        <div style={{ overflowX:'auto' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'14px' }}>
                            <thead>
                              <tr style={{ borderBottom:'2px solid #e5e7eb', background:'#f9fafb' }}>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Timestamp</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Severity</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Error Message</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Script</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>User</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {errorLogs.map(log => {
                                const severityColors = { CRITICAL: '#ef4444', ERROR: '#f59e0b', WARNING: '#3b82f6' };
                                const severityColor = severityColors[log.severity] || '#6b7280';
                                return (
                                  <tr key={log.errorId} onClick={()=>loadErrorLogDetail(orgId, log.errorId)} style={{ borderBottom:'1px solid #e5e7eb', cursor:'pointer' }} onMouseOver={(e)=>e.currentTarget.style.background='#f9fafb'} onMouseOut={(e)=>e.currentTarget.style.background='transparent'}>
                                    <td style={{ padding:'12px' }}>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
                                    <td style={{ padding:'12px' }}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: `${severityColor}20`, color: severityColor }}>{log.severity || '-'}</span></td>
                                    <td style={{ padding:'12px', maxWidth:'400px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.errorMessage || '-'}</td>
                                    <td style={{ padding:'12px' }}>{log.scriptName || '-'}</td>
                                    <td style={{ padding:'12px' }}>{log.userEmail || log.userId?.substring(0, 8) || '-'}</td>
                                    <td style={{ padding:'12px' }}>{log.occurrenceCount || 1}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16, paddingTop:16, borderTop:'1px solid #e5e7eb' }}>
                          <div style={{ fontSize:14, color:'#6b7280' }}>Showing {((errorLogsPage - 1) * errorLogsPageSize) + 1} to {Math.min(errorLogsPage * errorLogsPageSize, errorLogsTotal)} of {errorLogsTotal} results</div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button onClick={()=>setErrorLogsPage(p => Math.max(1, p - 1))} disabled={errorLogsPage === 1} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: errorLogsPage === 1 ? '#f3f4f6' : '#fff', color: errorLogsPage === 1 ? '#9ca3af' : '#111827', cursor: errorLogsPage === 1 ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Previous</button>
                            <button onClick={()=>setErrorLogsPage(p => p + 1)} disabled={errorLogsPage * errorLogsPageSize >= errorLogsTotal} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: errorLogsPage * errorLogsPageSize >= errorLogsTotal ? '#f3f4f6' : '#fff', color: errorLogsPage * errorLogsPageSize >= errorLogsTotal ? '#9ca3af' : '#111827', cursor: errorLogsPage * errorLogsPageSize >= errorLogsTotal ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Next</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {selectedErrorLog && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={()=>setSelectedErrorLog(null)}>
                      <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', width: '90%' }} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                          <h3 style={{ margin:0, fontSize:20, fontWeight:600 }}>Error Details</h3>
                          <button onClick={()=>setSelectedErrorLog(null)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
                        </div>
                        <div style={{ display:'grid', gap:16 }}>
                          <div><strong>Error ID:</strong> {selectedErrorLog.errorId}</div>
                          <div><strong>Timestamp:</strong> {selectedErrorLog.timestamp ? new Date(selectedErrorLog.timestamp).toLocaleString() : '-'}</div>
                          <div><strong>Severity:</strong> <span style={{ color: selectedErrorLog.severity === 'CRITICAL' ? '#ef4444' : selectedErrorLog.severity === 'ERROR' ? '#f59e0b' : '#3b82f6' }}>{selectedErrorLog.severity}</span></div>
                          <div><strong>Error Type:</strong> {selectedErrorLog.errorType || '-'}</div>
                          <div><strong>Error Code:</strong> {selectedErrorLog.errorCode || '-'}</div>
                          <div><strong>Error Message:</strong> {selectedErrorLog.errorMessage || '-'}</div>
                          {selectedErrorLog.stackTrace && <div><strong>Stack Trace:</strong><pre style={{ background:'#f3f4f6', padding:12, borderRadius:8, overflow:'auto', fontSize:12, maxHeight:300 }}>{selectedErrorLog.stackTrace}</pre></div>}
                          <div><strong>User:</strong> {selectedErrorLog.userEmail || selectedErrorLog.userId || '-'}</div>
                          <div><strong>Script:</strong> {selectedErrorLog.scriptName || '-'} {selectedErrorLog.scriptVersion ? `(${selectedErrorLog.scriptVersion})` : ''}</div>
                          <div><strong>Occurrence Count:</strong> {selectedErrorLog.occurrenceCount || 1}</div>
                          {selectedErrorLog.resolutionStatus && <div><strong>Resolution Status:</strong> {selectedErrorLog.resolutionStatus}</div>}
                          {selectedErrorLog.resolutionNotes && <div><strong>Resolution Notes:</strong> {selectedErrorLog.resolutionNotes}</div>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {systemLogsView==='api' && (
                <div style={{ display:'grid', gap:12 }}>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Filters</h3>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Start Date</span>
                        <input type="date" value={apiLogsFilters.startDate ? apiLogsFilters.startDate.toISOString().split('T')[0] : ''} onChange={e=>setApiLogsFilters({...apiLogsFilters, startDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>End Date</span>
                        <input type="date" value={apiLogsFilters.endDate ? apiLogsFilters.endDate.toISOString().split('T')[0] : ''} onChange={e=>setApiLogsFilters({...apiLogsFilters, endDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Direction</span>
                        <select value={apiLogsFilters.direction} onChange={e=>setApiLogsFilters({...apiLogsFilters, direction: e.target.value})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff', cursor: 'pointer' }}>
                          <option value="">All</option>
                          <option value="INBOUND">Inbound</option>
                          <option value="OUTBOUND">Outbound</option>
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>HTTP Method</span>
                        <select value={apiLogsFilters.httpMethod} onChange={e=>setApiLogsFilters({...apiLogsFilters, httpMethod: e.target.value})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff', cursor: 'pointer' }}>
                          <option value="">All</option>
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="DELETE">DELETE</option>
                          <option value="PATCH">PATCH</option>
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Search</span>
                        <input type="text" value={apiLogsSearch} onChange={e=>setApiLogsSearch(e.target.value)} placeholder="Search endpoints" style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                    </div>
                    <button onClick={()=>loadApiLogs(orgId)} style={{ marginTop: 12, padding: '10px 20px', background: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>Apply Filters</button>
                  </div>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>API Logs</h3>
                    {loadingApiLogs ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>Loading...</div> : apiLogs.length === 0 ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No API logs found</div> : (
                      <>
                        <div style={{ overflowX:'auto' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'14px' }}>
                            <thead>
                              <tr style={{ borderBottom:'2px solid #e5e7eb', background:'#f9fafb' }}>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Timestamp</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Method</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Endpoint</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Status</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Time (ms)</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Size</th>
                              </tr>
                            </thead>
                            <tbody>
                              {apiLogs.map(log => (
                                <tr key={log.apiLogId} onClick={()=>loadApiLogDetail(orgId, log.apiLogId)} style={{ borderBottom:'1px solid #e5e7eb', cursor:'pointer' }} onMouseOver={(e)=>e.currentTarget.style.background='#f9fafb'} onMouseOut={(e)=>e.currentTarget.style.background='transparent'}>
                                  <td style={{ padding:'12px' }}>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
                                  <td style={{ padding:'12px' }}>{log.method || '-'}</td>
                                  <td style={{ padding:'12px', maxWidth:'300px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.endpoint || '-'}</td>
                                  <td style={{ padding:'12px' }}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: log.statusCode >= 200 && log.statusCode < 300 ? '#10b98120' : log.statusCode >= 400 ? '#ef444420' : '#6b728020', color: log.statusCode >= 200 && log.statusCode < 300 ? '#10b981' : log.statusCode >= 400 ? '#ef4444' : '#6b7280' }}>{log.statusCode || '-'}</span></td>
                                  <td style={{ padding:'12px' }}>{log.responseTimeMs ? `${log.responseTimeMs}ms` : '-'}</td>
                                  <td style={{ padding:'12px' }}>{log.responseSizeBytes ? `${(log.responseSizeBytes / 1024).toFixed(2)} KB` : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16, paddingTop:16, borderTop:'1px solid #e5e7eb' }}>
                          <div style={{ fontSize:14, color:'#6b7280' }}>Showing {((apiLogsPage - 1) * apiLogsPageSize) + 1} to {Math.min(apiLogsPage * apiLogsPageSize, apiLogsTotal)} of {apiLogsTotal} results</div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button onClick={()=>setApiLogsPage(p => Math.max(1, p - 1))} disabled={apiLogsPage === 1} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: apiLogsPage === 1 ? '#f3f4f6' : '#fff', color: apiLogsPage === 1 ? '#9ca3af' : '#111827', cursor: apiLogsPage === 1 ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Previous</button>
                            <button onClick={()=>setApiLogsPage(p => p + 1)} disabled={apiLogsPage * apiLogsPageSize >= apiLogsTotal} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: apiLogsPage * apiLogsPageSize >= apiLogsTotal ? '#f3f4f6' : '#fff', color: apiLogsPage * apiLogsPageSize >= apiLogsTotal ? '#9ca3af' : '#111827', cursor: apiLogsPage * apiLogsPageSize >= apiLogsTotal ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Next</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {selectedApiLog && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={()=>setSelectedApiLog(null)}>
                      <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', width: '90%' }} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                          <h3 style={{ margin:0, fontSize:20, fontWeight:600 }}>API Log Details</h3>
                          <button onClick={()=>setSelectedApiLog(null)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
                        </div>
                        <div style={{ display:'grid', gap:16 }}>
                          <div><strong>API Log ID:</strong> {selectedApiLog.apiLogId}</div>
                          <div><strong>Timestamp:</strong> {selectedApiLog.timestamp ? new Date(selectedApiLog.timestamp).toLocaleString() : '-'}</div>
                          <div><strong>Direction:</strong> {selectedApiLog.direction}</div>
                          <div><strong>Method:</strong> {selectedApiLog.httpMethod || '-'}</div>
                          <div><strong>Endpoint:</strong> {selectedApiLog.endpoint || '-'}</div>
                          <div><strong>Status Code:</strong> {selectedApiLog.responseStatusCode || '-'}</div>
                          <div><strong>Response Time:</strong> {selectedApiLog.responseTimeMs ? `${selectedApiLog.responseTimeMs}ms` : '-'}</div>
                          <div><strong>User:</strong> {selectedApiLog.userEmail || selectedApiLog.userId || '-'}</div>
                          {selectedApiLog.errorOccurred && <div style={{ color:'#ef4444' }}><strong>Error:</strong> {selectedApiLog.errorMessage || 'Unknown error'}</div>}
                          {selectedApiLog.requestBody && <div><strong>Request Body:</strong><pre style={{ background:'#f3f4f6', padding:12, borderRadius:8, overflow:'auto', fontSize:12, maxHeight:200 }}>{selectedApiLog.requestBody}</pre></div>}
                          {selectedApiLog.responseBody && <div><strong>Response Body:</strong><pre style={{ background:'#f3f4f6', padding:12, borderRadius:8, overflow:'auto', fontSize:12, maxHeight:200 }}>{selectedApiLog.responseBody}</pre></div>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {systemLogsView==='system-event' && (
                <div style={{ display:'grid', gap:12 }}>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Filters</h3>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Start Date</span>
                        <input type="date" value={systemEventLogsFilters.startDate ? systemEventLogsFilters.startDate.toISOString().split('T')[0] : ''} onChange={e=>setSystemEventLogsFilters({...systemEventLogsFilters, startDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>End Date</span>
                        <input type="date" value={systemEventLogsFilters.endDate ? systemEventLogsFilters.endDate.toISOString().split('T')[0] : ''} onChange={e=>setSystemEventLogsFilters({...systemEventLogsFilters, endDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Severity</span>
                        <select value={systemEventLogsFilters.severity} onChange={e=>setSystemEventLogsFilters({...systemEventLogsFilters, severity: e.target.value})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff', cursor: 'pointer' }}>
                          <option value="">All</option>
                          <option value="CRITICAL">Critical</option>
                          <option value="ERROR">Error</option>
                          <option value="WARNING">Warning</option>
                          <option value="INFO">Info</option>
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Search</span>
                        <input type="text" value={systemEventLogsSearch} onChange={e=>setSystemEventLogsSearch(e.target.value)} placeholder="Search events" style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                    </div>
                    <button onClick={()=>loadSystemEventLogs(orgId)} style={{ marginTop: 12, padding: '10px 20px', background: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>Apply Filters</button>
                  </div>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>System Event Logs</h3>
                    {loadingSystemEventLogs ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>Loading...</div> : systemEventLogs.length === 0 ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No system event logs found</div> : (
                      <>
                        <div style={{ overflowX:'auto' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'14px' }}>
                            <thead>
                              <tr style={{ borderBottom:'2px solid #e5e7eb', background:'#f9fafb' }}>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Timestamp</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Severity</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Event</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Actor</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {systemEventLogs.map(log => {
                                const severityColors = { CRITICAL: '#ef4444', ERROR: '#f59e0b', WARNING: '#3b82f6', INFO: '#10b981' };
                                const severityColor = severityColors[log.severity] || '#6b7280';
                                return (
                                  <tr key={log.eventId} onClick={()=>loadSystemEventLogDetail(orgId, log.eventId)} style={{ borderBottom:'1px solid #e5e7eb', cursor:'pointer' }} onMouseOver={(e)=>e.currentTarget.style.background='#f9fafb'} onMouseOut={(e)=>e.currentTarget.style.background='transparent'}>
                                    <td style={{ padding:'12px' }}>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
                                    <td style={{ padding:'12px' }}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: `${severityColor}20`, color: severityColor }}>{log.severity || '-'}</span></td>
                                    <td style={{ padding:'12px', maxWidth:'300px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.eventName || '-'}</td>
                                    <td style={{ padding:'12px' }}>{log.actorEmail || log.actorId?.substring(0, 8) || '-'}</td>
                                    <td style={{ padding:'12px' }}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: log.result === 'SUCCESS' ? '#10b98120' : log.result === 'FAILURE' ? '#ef444420' : '#6b728020', color: log.result === 'SUCCESS' ? '#10b981' : log.result === 'FAILURE' ? '#ef4444' : '#6b7280' }}>{log.result || '-'}</span></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16, paddingTop:16, borderTop:'1px solid #e5e7eb' }}>
                          <div style={{ fontSize:14, color:'#6b7280' }}>Showing {((systemEventLogsPage - 1) * systemEventLogsPageSize) + 1} to {Math.min(systemEventLogsPage * systemEventLogsPageSize, systemEventLogsTotal)} of {systemEventLogsTotal} results</div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button onClick={()=>setSystemEventLogsPage(p => Math.max(1, p - 1))} disabled={systemEventLogsPage === 1} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: systemEventLogsPage === 1 ? '#f3f4f6' : '#fff', color: systemEventLogsPage === 1 ? '#9ca3af' : '#111827', cursor: systemEventLogsPage === 1 ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Previous</button>
                            <button onClick={()=>setSystemEventLogsPage(p => p + 1)} disabled={systemEventLogsPage * systemEventLogsPageSize >= systemEventLogsTotal} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: systemEventLogsPage * systemEventLogsPageSize >= systemEventLogsTotal ? '#f3f4f6' : '#fff', color: systemEventLogsPage * systemEventLogsPageSize >= systemEventLogsTotal ? '#9ca3af' : '#111827', cursor: systemEventLogsPage * systemEventLogsPageSize >= systemEventLogsTotal ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Next</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {selectedSystemEventLog && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={()=>setSelectedSystemEventLog(null)}>
                      <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', width: '90%' }} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                          <h3 style={{ margin:0, fontSize:20, fontWeight:600 }}>System Event Details</h3>
                          <button onClick={()=>setSelectedSystemEventLog(null)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
                        </div>
                        <div style={{ display:'grid', gap:16 }}>
                          <div><strong>Event ID:</strong> {selectedSystemEventLog.eventId}</div>
                          <div><strong>Timestamp:</strong> {selectedSystemEventLog.timestamp ? new Date(selectedSystemEventLog.timestamp).toLocaleString() : '-'}</div>
                          <div><strong>Event Type:</strong> {selectedSystemEventLog.eventType || '-'}</div>
                          <div><strong>Event Name:</strong> {selectedSystemEventLog.eventName || '-'}</div>
                          <div><strong>Severity:</strong> <span style={{ color: selectedSystemEventLog.eventSeverity === 'CRITICAL' ? '#ef4444' : selectedSystemEventLog.eventSeverity === 'ERROR' ? '#f59e0b' : selectedSystemEventLog.eventSeverity === 'WARNING' ? '#3b82f6' : '#10b981' }}>{selectedSystemEventLog.eventSeverity || '-'}</span></div>
                          <div><strong>Actor:</strong> {selectedSystemEventLog.actorEmail || selectedSystemEventLog.actorName || selectedSystemEventLog.actorId || '-'}</div>
                          <div><strong>Action:</strong> {selectedSystemEventLog.action || '-'}</div>
                          <div><strong>Result:</strong> {selectedSystemEventLog.result || '-'}</div>
                          {selectedSystemEventLog.targetEntityName && <div><strong>Target:</strong> {selectedSystemEventLog.targetEntityName}</div>}
                          {selectedSystemEventLog.failureReason && <div style={{ color:'#ef4444' }}><strong>Failure Reason:</strong> {selectedSystemEventLog.failureReason}</div>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {systemLogsView==='export' && (
                <div style={{ display:'grid', gap:12 }}>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Filters</h3>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12 }}>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Start Date</span>
                        <input type="date" value={exportLogsFilters.startDate ? exportLogsFilters.startDate.toISOString().split('T')[0] : ''} onChange={e=>setExportLogsFilters({...exportLogsFilters, startDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>End Date</span>
                        <input type="date" value={exportLogsFilters.endDate ? exportLogsFilters.endDate.toISOString().split('T')[0] : ''} onChange={e=>setExportLogsFilters({...exportLogsFilters, endDate: e.target.value ? new Date(e.target.value) : null})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Export Format</span>
                        <select value={exportLogsFilters.exportFormat} onChange={e=>setExportLogsFilters({...exportLogsFilters, exportFormat: e.target.value})} style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff', cursor: 'pointer' }}>
                          <option value="">All</option>
                          <option value="CSV">CSV</option>
                          <option value="JSON">JSON</option>
                          <option value="PDF">PDF</option>
                          <option value="XLSX">XLSX</option>
                        </select>
                      </label>
                      <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Search</span>
                        <input type="text" value={exportLogsSearch} onChange={e=>setExportLogsSearch(e.target.value)} placeholder="Search exports" style={{ padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', background: '#fff' }} />
                      </label>
                    </div>
                    <button onClick={()=>loadExportLogs(orgId)} style={{ marginTop: 12, padding: '10px 20px', background: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>Apply Filters</button>
                  </div>
                  <div className="dashboard-card" style={{ padding:16 }}>
                    <h3 style={{ marginTop:0, marginBottom:16, fontSize:18, fontWeight:600 }}>Export Logs</h3>
                    {loadingExportLogs ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>Loading...</div> : exportLogs.length === 0 ? <div style={{ color:'#6b7280', padding:'20px 0', textAlign:'center' }}>No export logs found</div> : (
                      <>
                        <div style={{ overflowX:'auto' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'14px' }}>
                            <thead>
                              <tr style={{ borderBottom:'2px solid #e5e7eb', background:'#f9fafb' }}>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Timestamp</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Export Type</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Format</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>User</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Records</th>
                                <th style={{ padding:'12px', textAlign:'left', fontWeight:600 }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {exportLogs.map(log => (
                                <tr key={log.exportId} onClick={()=>loadExportLogDetail(orgId, log.exportId)} style={{ borderBottom:'1px solid #e5e7eb', cursor:'pointer' }} onMouseOver={(e)=>e.currentTarget.style.background='#f9fafb'} onMouseOut={(e)=>e.currentTarget.style.background='transparent'}>
                                  <td style={{ padding:'12px' }}>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
                                  <td style={{ padding:'12px' }}>{log.exportType || '-'}</td>
                                  <td style={{ padding:'12px' }}>{log.exportFormat || '-'}</td>
                                  <td style={{ padding:'12px' }}>{log.userEmail || log.userId?.substring(0, 8) || '-'}</td>
                                  <td style={{ padding:'12px' }}>{log.recordCount ? log.recordCount.toLocaleString() : '-'}</td>
                                  <td style={{ padding:'12px' }}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: log.exportStatus === 'COMPLETED' ? '#10b98120' : log.exportStatus === 'FAILED' ? '#ef444420' : '#6b728020', color: log.exportStatus === 'COMPLETED' ? '#10b981' : log.exportStatus === 'FAILED' ? '#ef4444' : '#6b7280' }}>{log.exportStatus || '-'}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16, paddingTop:16, borderTop:'1px solid #e5e7eb' }}>
                          <div style={{ fontSize:14, color:'#6b7280' }}>Showing {((exportLogsPage - 1) * exportLogsPageSize) + 1} to {Math.min(exportLogsPage * exportLogsPageSize, exportLogsTotal)} of {exportLogsTotal} results</div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button onClick={()=>setExportLogsPage(p => Math.max(1, p - 1))} disabled={exportLogsPage === 1} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: exportLogsPage === 1 ? '#f3f4f6' : '#fff', color: exportLogsPage === 1 ? '#9ca3af' : '#111827', cursor: exportLogsPage === 1 ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Previous</button>
                            <button onClick={()=>setExportLogsPage(p => p + 1)} disabled={exportLogsPage * exportLogsPageSize >= exportLogsTotal} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', background: exportLogsPage * exportLogsPageSize >= exportLogsTotal ? '#f3f4f6' : '#fff', color: exportLogsPage * exportLogsPageSize >= exportLogsTotal ? '#9ca3af' : '#111827', cursor: exportLogsPage * exportLogsPageSize >= exportLogsTotal ? 'not-allowed' : 'pointer', fontSize: '14px' }}>Next</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {selectedExportLog && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={()=>setSelectedExportLog(null)}>
                      <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto', width: '90%' }} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                          <h3 style={{ margin:0, fontSize:20, fontWeight:600 }}>Export Details</h3>
                          <button onClick={()=>setSelectedExportLog(null)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
                        </div>
                        <div style={{ display:'grid', gap:16 }}>
                          <div><strong>Export ID:</strong> {selectedExportLog.exportId}</div>
                          <div><strong>Timestamp:</strong> {selectedExportLog.timestamp ? new Date(selectedExportLog.timestamp).toLocaleString() : '-'}</div>
                          <div><strong>Export Type:</strong> {selectedExportLog.exportType || '-'}</div>
                          <div><strong>Format:</strong> {selectedExportLog.exportFormat || '-'}</div>
                          <div><strong>User:</strong> {selectedExportLog.userEmail || selectedExportLog.userName || selectedExportLog.userId || '-'}</div>
                          <div><strong>Status:</strong> {selectedExportLog.exportStatus || '-'}</div>
                          <div><strong>Record Count:</strong> {selectedExportLog.recordCount ? selectedExportLog.recordCount.toLocaleString() : '-'}</div>
                          <div><strong>File Size:</strong> {selectedExportLog.fileSizeBytes ? `${(selectedExportLog.fileSizeBytes / 1024 / 1024).toFixed(2)} MB` : '-'}</div>
                          <div><strong>File Name:</strong> {selectedExportLog.fileName || '-'}</div>
                          {selectedExportLog.reason && <div><strong>Reason:</strong> {selectedExportLog.reason}</div>}
                          {selectedExportLog.reasonNotes && <div><strong>Reason Notes:</strong> {selectedExportLog.reasonNotes}</div>}
                          {selectedExportLog.containsPii && <div style={{ color:'#f59e0b' }}><strong>⚠ Contains PII</strong></div>}
                          {selectedExportLog.errorMessage && <div style={{ color:'#ef4444' }}><strong>Error:</strong> {selectedExportLog.errorMessage}</div>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {monitoringSubTab==='real-time' && (
            <RealTimeDashboardWrapper orgId={orgId} />
          )}

          {monitoringSubTab==='performance' && (
            <PerformanceMonitoring orgId={orgId} />
          )}

          {monitoringSubTab==='alerting' && (
            <AlertingSystem key={`alerting-${orgId}`} orgId={orgId} />
          )}

          {monitoringSubTab==='user-analytics' && (
            <UserAnalytics orgId={orgId} />
          )}

          {monitoringSubTab==='script-analytics' && (
            <ScriptAnalytics orgId={orgId} />
          )}

          {monitoringSubTab==='custom-reports' && (
            <CustomReports orgId={orgId} />
          )}
        </div>
      )}
      {showPurchaseModal && orgId && (
        <CreditPurchaseModal
          organizationId={orgId}
          onSuccess={() => {
            setShowPurchaseModal(false);
            if (orgId) loadCreditBalance(orgId);
          }}
          onClose={() => setShowPurchaseModal(false)}
        />
      )}
      
      {showAllocationModal && creditBalance && creditBalance.organization && orgId && (
        <CreditAllocationModal
          organizationId={orgId}
          teamId={null}
          sourceAccountId={creditBalance.organization?.account_id}
          availableBalance={creditBalance.organization?.unallocated || 0}
          teams={availableTeams}
          users={availableUsers}
          onSuccess={() => {
            setShowAllocationModal(false);
            if (orgId) loadCreditBalance(orgId);
          }}
          onClose={() => setShowAllocationModal(false)}
        />
      )}
    </div>
  );
};

export default EnterprisePolicy;


